import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Phone,
  MessageSquare,
  Send,
  Users,
  Calendar,
  CheckCircle2,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Zap,
  Brain,
  Clock,
  Loader2,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  Flame,
  Snowflake,
  Target,
  Shield,
  ArrowRight,
  ArrowLeft,
  Sparkles,
  Activity,
  BarChart3,
  MessageCircle,
  XCircle,
  Pause,
  Play,
  MoreHorizontal,
  Copy,
  ExternalLink,
  ClipboardList,
  Mic,
  MicOff,
  Pin,
  PinOff,
  RefreshCw,
  FileText,
  User
} from 'lucide-react';
import clsx from 'clsx';
import { jsPDF } from 'jspdf';
import fetchWithCsrf from '../lib/fetchWithCsrf';
import { wasNeverSent } from '../lib/messageVisibility';
import supabase from '../lib/supabaseClient';
import { useAuth } from '../context/AuthContext';
import useTwilioCall from '../hooks/useTwilioCall';
import EscalationModal from '../components/EscalationModal';

// ============================================================================
// SNOOZE
// ============================================================================
// A snooze never expires. leads.snoozed_until is still the storage and the wire
// format — every server-side suppression gate already reads "future timestamp =
// stay quiet" — so "forever" is a date that never arrives. Keeping the column
// means the five deployed edge functions need no change and no migration is required.
// MIRRORED in src/pages/DashboardV2.jsx; change both together.
const SNOOZE_FOREVER = '9999-12-31T00:00:00.000Z';
// Legacy dated snoozes (the old 1–30 day picker) still expire on their own — correct,
// since that is what the rep was promised when they set them. DashboardV2 tells the two
// apart to render "until you wake them" vs a real date; here the state is binary.
const isSnoozed = (lead) => !!lead?.snoozed_until && new Date(lead.snoozed_until).getTime() > Date.now();

// ============================================================================
// MICRO COMPONENTS (unchanged)
// ============================================================================

const PulseDot = ({ color = '#22C55E', size = 8 }) => (
  <span className="relative inline-flex">
    <span 
      className="absolute inline-flex h-full w-full rounded-full opacity-75 animate-ping"
      style={{ backgroundColor: color }}
    />
    <span 
      className="relative inline-flex rounded-full"
      style={{ backgroundColor: color, width: size, height: size }}
    />
  </span>
);

// Inbound MMS attachments.
//
// The bytes live at a Twilio media URL that requires Basic auth, so they cannot be
// dropped into an <img src> and the credentials can never reach the browser. Each
// attachment is pulled through the authenticated Express proxy
// (GET /api/messages/:id/media/:index) and handed to the <img> as an object URL.
const MessageAttachments = ({ messageId, media, tone = 'inbound' }) => {
  const [items, setItems] = useState(() => media.map(m => ({ ...m, url: null, failed: false })));
  const [reloadKey, setReloadKey] = useState(0);
  // groupMessages rebuilds the media array on every render, so keying the fetch
  // on the array identity would re-download the images each time.
  const mediaKey = useMemo(() => JSON.stringify(media), [media]);

  useEffect(() => {
    let cancelled = false;
    const created = [];
    setItems(media.map(m => ({ ...m, url: null, failed: false })));

    // fetchWithCsrf sends a custom `auth_token` from localStorage that rotates
    // out from under it, so the first call on a freshly-opened page can eat a
    // 401 and only recover on the request after it. One retry keeps a transient
    // failure from permanently blanking an attachment the lead actually sent.
    async function load(i) {
      for (let attempt = 0; attempt < 2; attempt++) {
        if (cancelled) return null;
        try {
          const resp = await fetchWithCsrf(`/api/messages/${messageId}/media/${i}`);
          if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
          return await resp.blob();
        } catch (err) {
          console.warn(`Attachment ${i} load attempt ${attempt + 1} failed:`, err.message);
          if (attempt === 0) await new Promise(r => setTimeout(r, 400));
        }
      }
      return null;
    }

    (async () => {
      for (let i = 0; i < media.length; i++) {
        const blob = await load(i);
        if (cancelled) return;
        if (!blob) {
          setItems(prev => prev.map((it, j) => (j === i ? { ...it, failed: true } : it)));
          continue;
        }
        const objectUrl = URL.createObjectURL(blob);
        created.push(objectUrl);
        setItems(prev => prev.map((it, j) => (j === i ? { ...it, url: objectUrl } : it)));
      }
    })();

    return () => {
      cancelled = true;
      created.forEach(u => URL.revokeObjectURL(u));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageId, mediaKey, reloadKey]);

  if (!media || media.length === 0) return null;

  const isImage = (t) => (t || '').startsWith('image/');
  const muted = tone === 'outbound' ? 'text-orange-100' : 'text-gray-400';

  return (
    <div className="mt-1.5 space-y-1.5">
      {items.map((item, i) => {
        if (item.failed) {
          return (
            <button
              key={i}
              type="button"
              onClick={() => setReloadKey(k => k + 1)}
              className={clsx(
                "flex items-center gap-1.5 text-xs hover:underline",
                muted
              )}
            >
              <AlertTriangle className="w-3 h-3" />
              <span>Attachment didn't load — retry</span>
            </button>
          );
        }
        if (!item.url) {
          return (
            <div
              key={i}
              className={clsx(
                "rounded-xl bg-gray-100 animate-pulse",
                isImage(item.contentType) ? "w-40 h-32" : "w-40 h-8"
              )}
            />
          );
        }
        if (isImage(item.contentType)) {
          return (
            <a key={i} href={item.url} target="_blank" rel="noopener noreferrer" className="block">
              <img
                src={item.url}
                alt="Attachment from lead"
                className="rounded-xl max-h-56 w-auto max-w-full border border-gray-200 hover:opacity-90 transition-opacity cursor-zoom-in"
              />
            </a>
          );
        }
        return (
          <a
            key={i}
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            className={clsx(
              "inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-medium",
              tone === 'outbound'
                ? "border-orange-300 text-orange-50 hover:bg-orange-400/30"
                : "border-gray-200 text-gray-600 hover:bg-gray-50"
            )}
          >
            <FileText className="w-3 h-3" />
            {item.contentType || 'Attachment'}
            <ExternalLink className="w-3 h-3" />
          </a>
        );
      })}
    </div>
  );
};

const Sparkline = ({ data, color, height = 32 }) => {
  if (!data || data.length < 2) return null;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  
  const points = data.map((val, i) => {
    const x = (i / (data.length - 1)) * 100;
    const y = 100 - ((val - min) / range) * 100;
    return `${x},${y}`;
  }).join(' ');

  return (
    <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ height, width: '100%' }}>
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
};

const StateStep = ({ label, isActive, isComplete, color }) => (
  <div className="flex flex-col items-center">
    <div 
      className={clsx(
        "w-3 h-3 rounded-full transition-all duration-500",
        isComplete && "scale-100",
        isActive && "ring-4 ring-opacity-30",
        !isComplete && !isActive && "bg-gray-200"
      )}
      style={{ 
        backgroundColor: isComplete || isActive ? color : undefined,
        ringColor: isActive ? color : undefined
      }}
    />
    <span className={clsx(
      "text-[10px] mt-1.5 font-medium transition-colors",
      isActive ? "text-gray-900" : "text-gray-400"
    )}>
      {label}
    </span>
  </div>
);

const StateConnector = ({ isComplete, color }) => (
  <div className="flex-1 h-0.5 mx-1 mt-1.5 rounded-full bg-gray-200 overflow-hidden">
    <div 
      className="h-full transition-all duration-700 ease-out"
      style={{ 
        width: isComplete ? '100%' : '0%',
        backgroundColor: color
      }}
    />
  </div>
);

// ============================================================================
// DATA TRANSFORMATION HELPERS
// ============================================================================

// Map lead status to state progression.
//
// Every key here must be a value score-lead actually WRITES, because the `|| 'Cold'`
// fallback is silent: an unmapped status does not warn, it renders as Cold and reads
// exactly like a lead who never replied.
//
// That is what happened to 'Engaging'. The engagement-driven status ladder (score-lead
// L1962) shipped 2026-07-24 writing 'Engaging'; this map only had 'Engaged', so every
// lead at the TOP of the ladder displayed as Cold. Measured 2026-08-08: 187 live leads.
// Lead 5198981d had sent 10 replies, named a price, a motivation and a timeline, and
// still showed Cold on this page.
//
// Live status values as of 2026-08-08 (counts): Cold Lead 54446, dead 18008,
// Disqualified 373, Warm Lead 275, Engaging 187, Hot Lead 95, Lost 5, Role Mismatch 4.
// The terminal ones are listed explicitly rather than left to the fallback — they
// resolve to Cold either way, but a written-down mapping is auditable and the fallback
// is not.
function mapStatusToState(status) {
  const statusMap = {
    'new': 'Cold',
    'Cold Lead': 'Cold',
    'Warm Lead': 'Warm',
    'Engaging': 'Engaging',   // what score-lead writes
    'Engaged': 'Engaging',    // legacy rows / older writers
    'Hot Lead': 'Hot',
    'Hot': 'Hot',
    'Escalated': 'Escalated',
    'converted': 'Escalated',
    // Terminal / non-progressing — explicit, previously reached via the fallback.
    'dead': 'Cold',
    'Lost': 'Cold',
    'Disqualified': 'Cold',
    'disqualified': 'Cold',
    'Role Mismatch': 'Cold'
  };
  return statusMap[status] || 'Cold';
}

// Calculate urgency from lead scores
function calculateUrgency(leadScore, analytics) {
  if (!leadScore) return 'medium';
  
  const hotScore = leadScore.hot_score || 0;
  const requiresAttention = leadScore.requires_immediate_attention;
  
  if (requiresAttention || hotScore >= 80) return 'high';
  if (hotScore >= 50) return 'medium';
  return 'low';
}

// Generate recommendation based on state and signals
function generateRecommendation(lead, leadScore, analytics) {
  if (!lead) return 'AI handling';
  
  const isHot = lead.status === 'Hot Lead' || lead.status === 'Hot';
  const isEscalated = lead.handoff_triggered;
  const aiEnabled = lead.ai_conversation_enabled !== false;
  
  if (isHot || isEscalated) return 'Call now';
  if (!aiEnabled) return 'Needs review';

  return 'AI handling';
}

// Extract risk signals from analytics
function extractRiskSignals(analytics, leadScore) {
  const signals = [];
  
  if (analytics?.content_analysis) {
    const ca = analytics.content_analysis;
    if (ca.cognitive_biases?.includes('loss_aversion')) signals.push('loss concerns');
    if (ca.churn_signals?.length > 0) signals.push('churn risk');
  }
  
  if (leadScore) {
    if (leadScore.hesitation_score > 60) signals.push('high hesitation');
    if (leadScore.objection_score > 40) signals.push('objection detected');
    if (leadScore.alert_triggers?.pricing_inquiry) signals.push('pricing concerns');
  }
  
  return signals.slice(0, 3);
}

// Extract completed milestones
function extractMilestones(lead, leadScore, messages) {
  const milestones = [];
  
  const hasInbound = messages?.some(m => m.direction === 'inbound');
  if (hasInbound) milestones.push('First reply');
  
  if (leadScore?.funnel_stage && !['Cold', 'Unknown', 'No Response'].includes(leadScore.funnel_stage)) {
    milestones.push('Qualified');
  }
  
  if (lead?.scheduling_mode || lead?.requested_followup_date) {
    milestones.push('Schedule attempt');
  }
  
  // status 'Hot Lead' covers every handoff now — booked appointments AND callback /
  // info / referral requests all land here (see supabase/functions/_shared/handoff.ts).
  // Still gated on status rather than marked_hot_at so a stray timestamp alone can
  // never claim a milestone.
  if (lead?.status === 'Hot Lead') {
    milestones.push('Marked Hot');
  }
  
  return milestones.slice(0, 4);
}

// Generate AI insights from analytics data - REAL DATA ONLY
function generateInsights(analytics, leadScore, lead, messages = []) {
  const insights = [];
  const analyticsTimestamp = null;
  const hasMessages = messages.length > 0;

  // No conversation yet — don't fabricate insights from default scores
  if (!hasMessages) {
    return [{
      type: 'engagement',
      text: 'No messages yet. AI will analyze engagement signals once the conversation begins.',
      timestamp: null
    }];
  }

  if (!analytics && !leadScore) {
    return [{
      type: 'engagement',
      text: 'Analyzing conversation patterns to identify engagement signals and buying intent.',
      timestamp: null
    }];
  }

  // Engagement trend insight - REAL DATA
  const engagementChange = analytics?.engagement_timeline?.engagement_changes;
  if (engagementChange !== undefined && engagementChange !== null) {
    if (engagementChange > 15) {
      insights.push({
        type: 'engagement',
        text: `Lead responded quickly multiple times in a row — high engagement pattern detected.`,
        delta: Math.round(engagementChange),
        timestamp: analyticsTimestamp
      });
    } else if (engagementChange > 0) {
      insights.push({
        type: 'engagement',
        text: `Engagement is trending upward — lead is becoming more responsive.`,
        delta: Math.round(engagementChange),
        timestamp: analyticsTimestamp
      });
    } else if (engagementChange < -10) {
      insights.push({
        type: 'engagement',
        text: `Engagement has cooled — consider changing approach or reaching out at a different time.`,
        delta: Math.round(engagementChange),
        timestamp: analyticsTimestamp
      });
    }
  }

  // Motivation insight - REAL DATA
  const motivationChange = analytics?.engagement_timeline?.motivation_changes;
  if (leadScore?.motivation_score > 70) {
    insights.push({
      type: 'motivation',
      text: `Strong motivation signal detected — lead appears eager to move forward and is actively engaged.`,
      delta: motivationChange ? Math.round(motivationChange) : null,
      timestamp: analyticsTimestamp
    });
  } else if (leadScore?.motivation_score > 50) {
    insights.push({
      type: 'motivation',
      text: `Moderate motivation detected — lead is interested but may need additional value proposition.`,
      delta: motivationChange ? Math.round(motivationChange) : null,
      timestamp: analyticsTimestamp
    });
  }

  // Emotional appeals insight - REAL DATA (no fake delta)
  const emotionalAppeals = analytics?.content_analysis?.emotional_appeals || [];
  if (emotionalAppeals.includes('urgency')) {
    insights.push({
      type: 'tone',
      text: `Lead responds well to time-sensitive messaging — urgency signals detected in conversation.`,
      timestamp: analyticsTimestamp
    });
  } else if (emotionalAppeals.includes('curiosity')) {
    insights.push({
      type: 'tone',
      text: `Lead shows curiosity signals — they're asking questions and want to learn more.`,
      timestamp: analyticsTimestamp
    });
  }

  // Conversion/buying signals insight - REAL DATA
  const conversionSignals = analytics?.predictive_indicators?.conversion_signals || [];
  if (conversionSignals.length > 0) {
    insights.push({
      type: 'buying',
      text: `Buying signals detected: lead is asking about ${conversionSignals.join(' and ')}. They're evaluating options.`,
      timestamp: analyticsTimestamp
    });
  }

  // Hesitation insight - REAL DATA
  const hesitationChange = analytics?.engagement_timeline?.hesitation_changes;
  if (leadScore?.hesitation_score > 50) {
    const alertTriggers = leadScore.alert_triggers || {};
    if (alertTriggers.pricing_inquiry) {
      insights.push({
        type: 'risk',
        text: `Risk factor: Lead showed hesitation about pricing. Address value proposition and ROI upfront.`,
        delta: hesitationChange ? Math.round(hesitationChange) : null,
        timestamp: analyticsTimestamp
      });
    } else {
      insights.push({
        type: 'risk',
        text: `Risk factor: Hesitation signals detected. Consider proactively addressing common objections.`,
        delta: hesitationChange ? Math.round(hesitationChange) : null,
        timestamp: analyticsTimestamp
      });
    }
  }

  // Action recommendation - based on real state
  if (leadScore?.requires_immediate_attention || lead?.status === 'Hot Lead') {
    insights.push({
      type: 'action',
      text: `Suggested action: Call now while engagement is high. Lead has shown clear intent signals.`,
      timestamp: analyticsTimestamp
    });
  } else if (analytics?.predictive_indicators?.engagement_trend === 'declining') {
    insights.push({
      type: 'action',
      text: `Suggested action: Re-engage with a different approach. Engagement trend is declining.`,
      timestamp: analyticsTimestamp
    });
  } else if (analytics?.predictive_indicators?.recommended_approach) {
    insights.push({
      type: 'action',
      text: `Suggested approach: ${analytics.predictive_indicators.recommended_approach}.`,
      timestamp: analyticsTimestamp
    });
  }

  // Ensure we have at least one insight
// 🔥 APPOINTMENT BOOKED - This is huge, show it prominently
  if (lead?.hot_reason === 'appointment_booked') {
    insights.unshift({
      type: 'action',
      text: `Appointment booked! Lead confirmed a call time. This is a high-intent prospect ready to convert.`,
      timestamp: lead.marked_hot_at ? formatTimeAgo(lead.marked_hot_at) : null
    });
  }

  // Ensure we have at least one insight
  if (insights.length === 0) {
    insights.push({
      type: 'engagement',
      text: 'Conversation is progressing normally. AI is monitoring for engagement signals.',
      timestamp: analyticsTimestamp
    });
  }

  return insights.slice(0, 5);
}

// Group messages intelligently by conversation flow
// Group messages - milestone changes define group boundaries
function groupMessages(messages, timezone = 'America/New_York') {
  if (!messages || messages.length === 0) return [];

  const sorted = [...messages]
    .filter(m => m.message_body !== '__page_open__')
    // A row the lead never received is not part of their conversation. An outbound row is
    // inserted before the send, so a draft stranded by the edge wall clock or dropped from
    // the queue sits here looking exactly like a delivered text — and reads to whoever
    // opens the thread as a message we sent. See src/lib/messageVisibility.js for what
    // counts, and for why carrier-rejected sends are deliberately still shown.
    .filter(m => !wasNeverSent(m))
    .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  const groups = [];

  function formatMsg(msg) {
    // Show the time the lead actually saw this message. Outbound replies can be
    // drafted (`timestamp`) hours before they send (`sent_at`) when held for
    // business hours — using the draft time hides that delay and makes the AI
    // look like it replied instantly. Prefer the real send time; for a held
    // message that hasn't sent yet, show its scheduled time; else the draft.
    const source = msg.direction === 'outbound'
      ? (msg.sent_at || (msg.business_hours_hold ? msg.scheduled_response_time : null) || msg.timestamp)
      : msg.timestamp;
    // Naive values (no offset) are stored as UTC; tz-aware values (…Z / +00:00)
    // are used as-is — appending 'Z' to those would produce an invalid date.
    const hasTz = /([zZ]|[+-]\d{2}:?\d{2})$/.test(source);
    const raw = hasTz ? source : source + 'Z';
    const d = new Date(raw);
    const now = new Date();
    const msgDateStr = d.toLocaleDateString('en-US', { timeZone: timezone });
    const todayStr = now.toLocaleDateString('en-US', { timeZone: timezone });
    const timeStr = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: timezone });
    // Show date for messages not from today
    const displayTime = msgDateStr === todayStr
      ? timeStr
      : `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: timezone })}, ${timeStr}`;

    const meta = msg.metadata || {};
    // Inbound MMS attachments, recorded by handleincomingmessage. A caption-less
    // MMS stores a generated placeholder body ("[Sent an image]") — with the
    // attachment itself rendered, repeating that line is just noise, so drop it.
    // A real caption sent alongside the image is kept.
    const media = Array.isArray(meta.media) ? meta.media : [];
    const body = msg.message_body || '';
    const isMediaPlaceholder = media.length > 0 && /^\[Sent [^\]]+\]$/.test(body.trim());
    return {
      id: msg.id,
      direction: msg.direction,
      text: isMediaPlaceholder ? '' : body,
      media,
      time: displayTime,
      isAI: msg.direction === 'outbound' && msg.sender !== 'human' && msg.sender !== 'Manual',
      isManual: msg.direction === 'outbound' && (msg.sender === 'human' || msg.sender === 'Manual'),
      isFollowUp: meta.function === 'ai-followup-outreach',
      isInitialOutreach: meta.function === 'ai-initial-outreach',
      followUpStage: meta.follow_up_stage || null,
      status: msg.status,
      channel: msg.channel || 'sms',
      businessHoursHold: msg.business_hours_hold || false,
      scheduledResponseTime: msg.scheduled_response_time || null,
    };
  }
  
  function detectMilestone(text) {
  if (!text) return null;
  const lower = text.toLowerCase().trim();
  const len = lower.length;

  // Test an array of patterns — strings use .includes(), RegExp use .test()
  const any = (patterns) => patterns.some(p =>
    p instanceof RegExp ? p.test(lower) : lower.includes(p)
  );

  // ============================================================
  // OPT-OUT / STOP (check first - highest priority)
  // ============================================================
  if (/^(stop|unsubscribe|remove|opt.?out|cancel|quit|end)$/i.test(lower) ||
      any(['stop texting', 'stop messaging', 'remove me', 'take me off',
           "don't text", 'dont text', "don't contact", 'dont contact',
           'leave me alone', 'not interested anymore'])) {
    return { type: 'optout', label: 'Opt-Out Request', highlight: { text: 'Unsubscribed', type: 'negative' } };
  }

  // ============================================================
  // SCHEDULING (time-related, availability, booking)
  // ============================================================
  // Time patterns (9am, 10:30, 2 pm, etc.)
  const hasTimePattern = /\b\d{1,2}\s*:?\s*\d{0,2}\s*(am|pm|a\.m\.|p\.m\.)\b/i.test(lower) ||
                         /\b(at|around|by)\s+\d{1,2}\b/.test(lower);

  if (hasTimePattern || any([
    // Direct scheduling phrases
    'schedule', 'call me', 'call you', 'good time', 'when can', 'set up a',
    'able to talk', 'free to talk', 'available to', 'can we talk', 'can we chat',
    "let's talk", 'lets talk', "let's chat", 'lets chat', 'have time',
    'when are you', 'when is good', 'what time', 'best time', 'convenient time',
    'book a', 'booking', 'appointment',
    /\bmeet(ing)?\b/,           // word-bounded: avoids "doesn't meet my needs" false... still fires but is OK
    // Days of week
    'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday',
    'tomorrow', 'this week', 'next week', 'this afternoon', 'this morning',
    'this evening', 'tonight',
    // Time slot responses
    'those times', 'that time', 'works for me', 'that works', 'sounds good for',
    "doesn't work", 'doesnt work', 'dont work', "don't work", "won't work", 'wont work',
    'none of those', 'different time', 'other time', 'another time',
    "i'm free", 'im free', 'i am free', "i'm available", 'im available',
    'i can do', 'i can make', 'works better',
    /\bprefer\b/,               // word-bounded: avoids matching inside "preference"
    // Confirmation
    'confirmed', 'see you', 'talk then', 'speak then', 'call then',
    'looking forward', "can't wait", 'cant wait',
    /\bconfirm\b/,              // word-bounded: avoids matching "confirmation email from..."
  ])) {
    return { type: 'scheduling', label: 'Scheduling', highlight: { text: 'Hot trigger', type: 'positive' } };
  }

  // ============================================================
  // BUYING SIGNALS (ready to purchase, commit, move forward)
  // ============================================================
  if (any([
    'sign up', 'signup', 'sign me up',
    /\bi'?m in[!.,?;\s]*$/,     // "i'm in!" only at end — not "i'm in construction/sales/etc."
    /\bcount me in\b/,
    "let's do it", 'lets do it', "let's go", 'lets go',
    'ready to start', 'ready to go', 'ready to move', 'move forward', 'proceed', 'get started',
    'start today', 'start now',
    /\b(enroll|register|subscribe)\b/,
    'i want to buy', "i'll take", 'ill take', "i'll buy", 'ill buy',
    'where do i sign', 'send me the contract', 'send the agreement', 'send me the paperwork',
    "what's next", 'whats next', 'next steps',
    'how do i pay',
    /\b(invoice|deposit)\b/,    // word-bounded: avoids "depositing" or partial matches
    "i'm ready", 'im ready',
    /\bsold\b(?=[!.,?;\s]*$)/,  // "sold!" or "sold." — not "I sold my car"
    /\bdeal\b(?=[!.,?;\s]*$)/,  // "deal!" at end — not "what's the deal?" or "deal breaker"
    'yes please',
    /\b(begin|start)\b(?!\w)/,  // word-bounded: avoids "beginning of..."
  ])) {
    return { type: 'buying', label: 'Buying Signal', highlight: { text: 'High intent', type: 'positive' } };
  }

  // ============================================================
  // PRICING INQUIRY
  // ============================================================
  if (any([
    'price', 'pricing', 'how much', "what's the cost", 'whats the cost',
    'expensive', 'cheap', 'affordable', 'budget', 'quote', 'estimate',
    'per month', 'monthly', 'per year', 'annually', 'one time', 'one-time',
    'payment plan', 'financing', 'discount', 'promo', 'promotion',
    'free trial', 'trial period', 'money back', 'refund', 'guarantee', 'roi',
    /\b(cost|costs|fee|fees)\b/,      // word-bounded: avoids "because of"
    /\b(rate|rates)\b/,               // word-bounded: avoids "at a great rate of speed"
    /\b(charge|charges)\b/,           // word-bounded: avoids "in charge of"
    /\bpayment\b/,                    // moved from buying — belongs here
    /\b(deal|value)\b/,               // "good deal?", "what's the value?" — pricing context
    /\b(spend|investment)\b/,         // word-bounded: avoids "I spend time with family"
    /\bworth it\b/,
  ])) {
    return { type: 'pricing', label: 'Pricing Discussion', highlight: { text: 'Pricing inquiry', type: 'info' } };
  }

  // ============================================================
  // COMPANY / IDENTITY INQUIRY (vetting)
  // ============================================================
  if (any([
    'who do you', 'what company', 'which company', 'who are you', 'who is this',
    'what is this', "who's this", 'whos this', 'your company', 'your business',
    'your organization', 'calling from', 'texting from',
    'what do you do', 'what does your', 'tell me about your', 'about your company',
    'your website', 'your site', 'more info', 'more information', 'learn more',
    'send me info', 'brochure', 'portfolio', 'credentials', 'references',
    'how did you get', 'where did you get',
    'is this legit', 'is this real', 'scam', 'spam', 'legitimate',
    /\brepresent\b/,                  // word-bounded
    /\bwork for\b/,                   // avoids "it doesn't work for me" (caught by scheduling first anyway)
    /\bmy (number|info)\b/,           // "how did you get my number?" — more specific
  ])) {
    return { type: 'company', label: 'Company Inquiry', highlight: { text: 'Vetting', type: 'info' } };
  }

  // ============================================================
  // HESITATION / OBJECTION
  // ============================================================
  if (any([
    'not sure', 'maybe later', 'think about it', 'thinking about it',
    'not ready', 'not right now', 'not at this time', 'bad time', 'busy right now',
    'too expensive', "can't afford", 'cant afford', 'out of budget',
    'need to talk to', 'check with', 'discuss with',
    'already have', 'already use', 'already using', 'current provider',
    'not interested', 'no thanks', 'no thank you', "i'll pass", 'ill pass',
    'not for me', "doesn't fit", 'doesnt fit', 'not a good fit',
    'hesitant', 'unsure',
    'tried before', "didn't work", 'didnt work', 'bad experience',
    'shopping around', 'comparing',
    'get back to you', 'follow up later', 'reach out later',
    'not now', 'some other time', 'another day',
    /\b(worried|worry|concern(ed)?)\b/,   // word-bounded
    /\bcompetitor\b/,
    /\bother option\b/,
    /\bask my\b/,                         // "need to ask my partner" — hesitation signal
    // "later" only when it clearly signals deferral, not "talk later!" (which is casual sign-off)
    /\b(maybe|probably|possibly)\s+later\b/,
    /\blater\s+(this\s+year|next\s+(week|month|year))\b/,
  ])) {
    return { type: 'hesitation', label: 'Hesitation', highlight: { text: 'Objection', type: 'warning' } };
  }

  // ============================================================
  // NEEDS EXPRESSED (specific requirements)
  // ============================================================
  if (len > 15 && any([
    // Explicit need phrases
    'looking for a', 'looking for an', 'need a', 'need an', 'hiring',
    'must have', 'need to have', 'should have',
    // Job/staffing — use phrases or clear context
    'full-time', 'full time', 'part-time', 'part time', 'temporary position',
    /\b(staffing|employee|candidate)\b/,
    /\blocum\b/,
    // Real estate — require specific phrases not bare nouns
    'sell my', 'selling my', 'buy a home', 'buying a home', 'buy a house', 'buying a house',
    'buy a property', 'buying a property',
    'bedroom', 'bathroom', 'sqft', 'square feet',
    'foreclosure', 'inherited property', 'relocating',
    // Job role titles — require context (len > 15 already filters short messages)
    /\b(contractor|freelance)\b/,
    /\b(dentist|hygienist|practitioner|therapist|technician)\b/,
    /\b(specialist|doctor|nurse)\b/,
    // Urgency
    /\basap\b/,
    /\burgent(ly)?\b/,
    'immediately', 'right away',
    /\bremote\s+(work|position|role|job)\b/,  // "remote work" not bare "remote"
    'on-site', 'onsite',
    // Requirement language
    'requirement', 'requirements', 'specification', 'criteria', 'preference',
  ])) {
    return { type: 'needs', label: 'Needs Expressed', highlight: { text: 'Requirements', type: 'positive' } };
  }

  // ============================================================
  // INTEREST EXPRESSED (general interest)
  // ============================================================
  if (any([
    'interested', 'curious', 'intrigued', 'tell me more',
    'want to know', 'like to know', 'want to learn', 'like to learn',
    'how does', 'how do you', 'how would', 'how can', 'what happens',
    'what would', 'what do i', 'can you explain', 'please explain',
    'sounds interesting', 'sounds good', 'sounds great', 'promising',
    'could use', 'could help', 'might need', 'might want', 'might be interested',
    'open to', 'willing to', 'considering', 'exploring', 'researching',
    /\binterest\b/,             // word-bounded: avoids "interesting" being missed and "disinterest"
  ])) {
    return { type: 'interest', label: 'Interest Expressed', highlight: { text: 'Engaged', type: 'positive' } };
  }

  // ============================================================
  // POSITIVE RESPONSE (agreement, affirmation)
  // ============================================================
  if (/^(yes|yeah|yep|yup|sure|ok|okay|k|yea|ya|yas)[!.,\s]*/i.test(lower) ||
      /^(sounds good|sounds great|sounds perfect|perfect|great|awesome|amazing|excellent|wonderful)[!.,\s]*/i.test(lower) ||
      /^(absolutely|definitely|certainly|of course|for sure|totally|100%)[!.,\s]*/i.test(lower) ||
      /^(love it|like it|i agree|agreed|correct|right|exactly|indeed)[!.,\s]*/i.test(lower) ||
      /^(please|please do|go ahead|do it|let's|lets)[!.,\s]*/i.test(lower) ||
      /^(thanks|thank you|thx|ty|appreciate)[!.,\s]*/i.test(lower)) {
    return { type: 'positive', label: 'Positive Response', highlight: { text: 'Engaged', type: 'positive' } };
  }

  // ============================================================
  // NEGATIVE RESPONSE (rejection, decline - but not opt-out)
  // ============================================================
  if (len < 50 && (
    /^(no|nope|nah|naw|negative)[!.,\s]*/i.test(lower) ||
    /^(don't|dont|do not|can't|cant|cannot|won't|wont|will not)\s/i.test(lower) ||
    /^(not really|not exactly|not quite)[!.,\s]*/i.test(lower)
  )) {
    return { type: 'negative', label: 'Negative Response', highlight: { text: 'Declined', type: 'warning' } };
  }

  // ============================================================
  // CALLBACK / CONTACT REQUEST
  // ============================================================
  if (any([
    'call me', 'give me a call', 'phone me', 'ring me', 'reach me',
    'contact me', 'get in touch', 'reach out', 'text me', 'email me',
    'my email', 'best number', 'best way to reach',
    'prefer email', 'prefer text', 'prefer call', 'easier to call',
    /\bmy number\b/,            // "here's my number" — more specific than bare 'my number'
    /\bsend me\b/,              // "send me info" — callback/interest, moved here from company
  ])) {
    return { type: 'callback', label: 'Contact Request', highlight: { text: 'Hot trigger', type: 'positive' } };
  }

  // ============================================================
  // REFERRAL MENTION
  // ============================================================
  if (any([
    'someone i know', 'know someone', 'referral', 'told me about', 'heard about',
    'mentioned you', 'passed along',
    /\b(refer|referred)\b/,     // word-bounded: avoids "I'll refer back to..."
    /\b(colleague|coworker|co-worker)\b/,
    /\bfriend\s+(of\s+mine|told|mentioned|said|referred)\b/,  // "a friend of mine" not "my friend is busy"
  ])) {
    return { type: 'referral', label: 'Referral Mention', highlight: { text: 'Referral', type: 'positive' } };
  }

  // ============================================================
  // TIMELINE / URGENCY
  // ============================================================
  if (any([
    'how soon', 'how long', 'how quickly', 'when can', 'when would',
    'time frame', 'timeframe', 'timeline', 'deadline', 'by when',
    'right away', 'immediately', 'rush',
    'this week', 'this month', 'next week', 'next month',
    'end of the month', 'end of the week', 'beginning of', 'start of',
    /\basap\b/,
    /\burgent\b/,
    /\bbefore\s+(the\s+end|my\s+deadline|the\s+deadline|next\s+\w+)\b/,  // "before the end" not bare "before"
  ])) {
    return { type: 'timeline', label: 'Timeline Discussion', highlight: { text: 'Urgency', type: 'info' } };
  }

  // ============================================================
  // LOCATION / SERVICE AREA
  // ============================================================
  if (any([
    'where are you', 'your location', 'your area', 'service area',
    'do you serve', 'do you cover', 'available in', 'come to',
    'based in', 'based out of', 'office in', 'near me',
    'my area', 'my city', 'my state', 'my zip', 'my location',
    'nearby', 'close by', 'in town',
    /\blocated\b/,              // word-bounded
    /\blocal\b/,                // word-bounded
  ])) {
    return { type: 'location', label: 'Location Question', highlight: { text: 'Service area', type: 'info' } };
  }

  // ============================================================
  // HOW IT WORKS / PROCESS QUESTION
  // ============================================================
  if (any([
    'how does it work', 'how do you work', 'how does this work',
    "what's the process", 'whats the process', 'your process',
    'what are the steps', 'step by step', 'walk me through',
    'explain how', 'tell me how', 'what happens next', 'what happens after',
    'then what',
  ])) {
    return { type: 'process', label: 'Process Question', highlight: { text: 'Discovery', type: 'info' } };
  }

  // ============================================================
  // GREETING (simple hello - low signal)
  // ============================================================
  if (len < 30 && (
    /^(hi|hey|hello|howdy|greetings|good morning|good afternoon|good evening)[!.,\s]*/i.test(lower) ||
    /^(what'?s up|whats up|sup|yo)[!.,\s]*/i.test(lower)
  )) {
    return { type: 'greeting', label: 'Greeting', highlight: null };
  }

  // ============================================================
  // CONFUSION / CLARIFICATION
  // ============================================================
  if (any([
    'what do you mean', "don't understand", 'dont understand', 'confused',
    'not clear', 'unclear', 'can you clarify', 'please clarify',
    'what are you', 'come again', "i don't get", 'i dont get', 'lost me', 'not following',
    /\bhuh\b/,                  // word-bounded: avoids matching inside other words
    /\bwhat\?/,
    /\bsorry\?/,
  ])) {
    return { type: 'confusion', label: 'Needs Clarification', highlight: { text: 'Confused', type: 'warning' } };
  }

  // ============================================================
  // GENERAL QUESTION (fallback for anything with ?)
  // ============================================================
  if (lower.includes('?') && len > 10) {
    return { type: 'question', label: 'Question', highlight: null };
  }

  // ============================================================
  // NO SPECIFIC MILESTONE DETECTED
  // ============================================================
  return null;
}
  
  function extractPreview(msgs) {
    const inbounds = msgs.filter(m => m.direction === 'inbound');
    if (inbounds.length === 0) return '';
    const best = inbounds.reduce((a, b) => 
      (b.message_body?.length || 0) > (a.message_body?.length || 0) ? b : a
    );
    const text = best.message_body || '';
    return text.length <= 55 ? text : text.substring(0, 55).trim() + '...';
  }

  // Find first inbound index
  const firstInboundIndex = sorted.findIndex(m => m.direction === 'inbound');
  
  // Group 1: Initial Outreach (everything before first reply)
  if (firstInboundIndex > 0) {
    const outreachMsgs = sorted.slice(0, firstInboundIndex);
    const formatted = outreachMsgs.map((m, idx) => {
      const fm = formatMsg(m);
      if (idx === 0) fm.isInitialOutreach = true;
      else fm.isFollowUp = true;
      return fm;
    });
    groups.push({
      id: `group-${groups.length}`,
      label: 'Initial Outreach',
      preview: outreachMsgs.length > 1 ? `${outreachMsgs.length} messages sent` : '',
      timestamp: formatTimeAgo(outreachMsgs[0].timestamp, timezone),
      highlight: null,
      messages: formatted
    });
  } else if (firstInboundIndex === -1) {
    // No inbound at all
    const formatted = sorted.map((m, idx) => {
      const fm = formatMsg(m);
      if (idx === 0) fm.isInitialOutreach = true;
      else fm.isFollowUp = true;
      return fm;
    });
    groups.push({
      id: `group-${groups.length}`,
      label: sorted.length > 1 ? 'Follow-up Attempts' : 'Initial Contact',
      preview: sorted.length > 1 ? `${sorted.length} messages sent, no reply` : '',
      timestamp: formatTimeAgo(sorted[0].timestamp, timezone),
      highlight: sorted.length > 1 ? { text: 'No response', type: 'neutral' } : null,
      messages: formatted
    });
    return groups;
  }

  // Process from first inbound onwards
  let currentGroup = null;
  let currentMilestoneType = null;
  
  for (let i = firstInboundIndex; i < sorted.length; i++) {
    const msg = sorted[i];
    
    if (msg.direction === 'inbound') {
      const milestone = detectMilestone(msg.message_body);
      
      // Start new group if: first group OR milestone type changed
      const milestoneChanged = milestone && milestone.type !== currentMilestoneType;
      const isFirstGroup = !currentGroup;
      
      if (isFirstGroup || milestoneChanged) {
        // Flush current group
        if (currentGroup) {
          groups.push(currentGroup);
        }
        
        // Start new group
        currentMilestoneType = milestone?.type || 'conversation';
        currentGroup = {
          id: `group-${groups.length}`,
          label: milestone?.label || 'Conversation',
          preview: '',
          timestamp: formatTimeAgo(msg.timestamp, timezone),
          highlight: milestone?.highlight || null,
          messages: []
        };
      }
      
      currentGroup.messages.push(formatMsg(msg));
      // Update preview to best inbound
      currentGroup.preview = extractPreview(
        currentGroup.messages.filter(m => m.direction === 'inbound').map(m => ({ message_body: m.text, direction: m.direction }))
      );
      
    } else {
      // Outbound
      if (currentGroup) {
        currentGroup.messages.push(formatMsg(msg));
      } else {
        // Orphan outbounds after last inbound group - follow-up attempts
        // Collect them all
        const followupMsgs = [];
        while (i < sorted.length) {
          followupMsgs.push(sorted[i]);
          i++;
        }
        if (followupMsgs.length > 0) {
          groups.push({
            id: `group-${groups.length}`,
            label: 'Follow-up Attempts',
            preview: `${followupMsgs.length} message${followupMsgs.length > 1 ? 's' : ''} sent, no reply`,
            timestamp: formatTimeAgo(followupMsgs[0].timestamp, timezone),
            highlight: { text: 'No response', type: 'neutral' },
            messages: followupMsgs.map(m => { const fm = formatMsg(m); fm.isFollowUp = true; return fm; })
          });
        }
        break;
      }
    }
  }
  
  // Flush last group
  if (currentGroup) {
    groups.push(currentGroup);
  }
  
  // Post-process: tag follow-up messages within each group
  // Logic: after an inbound, the first outbound is the AI reply.
  // Any additional consecutive outbounds (no inbound in between) are follow-ups.
  for (const group of groups) {
    let consecutiveOutbounds = 0;
    for (const msg of group.messages) {
      if (msg.direction === 'inbound') {
        consecutiveOutbounds = 0;
      } else if (msg.direction === 'outbound' && msg.isAI) {
        consecutiveOutbounds++;
        if (consecutiveOutbounds > 1) {
          msg.isFollowUp = true;
        }
      }
    }

    // If group has only outbounds (no inbound at all), relabel as follow-ups
    const hasInbound = group.messages.some(m => m.direction === 'inbound');
    if (!hasInbound && group.messages.length > 1) {
      group.label = 'Follow-up Attempts';
      group.preview = `${group.messages.length} message${group.messages.length > 1 ? 's' : ''} sent, no reply`;
      group.highlight = { text: 'No response', type: 'neutral' };
      // First message is initial outreach, rest are follow-ups
      group.messages.forEach((m, idx) => {
        if (m.direction === 'outbound' && idx > 0) m.isFollowUp = true;
        if (m.direction === 'outbound' && idx === 0) m.isInitialOutreach = true;
      });
    }
  }

  return groups;
}

// 🔴 NEVER RENDER `requested_followup_reason` (or any other internal reason field) IN
// THIS CARD. It is an INTERNAL field with many writers and no format contract, and it is
// shown to the TENANT. It used to be spliced in verbatim and lowercased as
// `mentioned "..."`, so opening a hot lead displayed
// `Lead shows mentioned "ai promised human contact with nothing booked: ..."` — the
// unbacked-contact-promise guard's own diagnostic (processmessageai ~L6864), presented as
// if the LEAD had said it.
//
// Measured 2026-08-09 across this tenant's 21 hot leads carrying a reason: 9 were raw
// internal diagnostics. The rest were operator notes or pasted lead text, which are no
// safer to show, because nothing distinguishes the two at render time. A keyword filter
// was tried and is what failed here: the guard's wording matched none of its blocked
// words. Any allowlist over free text written by a dozen callers is open by default.
//
// The tenant-safe source for "what does this lead want" is the CONVERSATION, which we
// already have. Quote the lead's last inbound message: it is always their own words, it
// can never contain internal text, and it needs no sanitising.
// Picks by TIMESTAMP, not array position: this component sorts `messages` locally in
// several places (see the `sorted` copies below), so incoming order is not guaranteed
// chronological and trusting it would quote an older message than the one on screen.
function getLastInboundQuote(messages) {
  if (!Array.isArray(messages) || messages.length === 0) return null;
  let best = null;
  let bestTime = -Infinity;
  for (const m of messages) {
    if (m?.direction !== 'inbound') continue;
    const body = typeof m.message_body === 'string' ? m.message_body.trim() : '';
    if (body.length < 3 || body === '__page_open__') continue;
    const t = new Date(m.timestamp).getTime();
    const when = Number.isFinite(t) ? t : -Infinity;
    if (when >= bestTime) { bestTime = when; best = body; }
  }
  if (!best) return null;
  return best.length > 120 ? `${best.slice(0, 117).trimEnd()}...` : best;
}

// Generate next step recommendation - NATURAL LANGUAGE
// Generate next step recommendation - CONTEXTUAL
function generateNextStep(lead, leadScore, analytics, messages) {
  const isHot = lead?.status === 'Hot Lead' || lead?.status === 'Hot';
  const isEscalated = lead?.handoff_triggered;
  const alertTriggers = leadScore?.alert_triggers || {};

  let action = 'Continue AI engagement';
  let reason = 'Lead is progressing through the qualification process. AI is handling the conversation effectively.';
  let script = null;
  let icon = Brain;
  let confidence = 3;
  let signals = 5;

  // Count active signals
  const activeSignals = Object.values(alertTriggers).filter(Boolean).length;
  signals = activeSignals + 5;

  // Extract personalization data
  const firstName = lead?.first_name || lead?.name?.split(' ')[0] || 'there';
  const customFields = lead?.custom_fields || {};
  const jobTitle = customFields['Job Title'];
  const city = customFields['Person City'];
  const industry = customFields['Secondary Industries'] || customFields['Primary Industry'];
  const escalationReason = lead?.escalation_reason;
  const lastInboundQuote = getLastInboundQuote(messages);

  if (isHot || isEscalated) {
    action = 'Call now, this lead is ready';
    
    // Build contextual reason
    const reasons = [];
    if (alertTriggers.buying_signal || escalationReason === 'buying signal') reasons.push('direct buying intent');
    if (alertTriggers.pricing_inquiry) reasons.push('pricing questions');
    if (alertTriggers.requested_callback) reasons.push('callback request');
    if (alertTriggers.agreed_to_meeting) reasons.push('agreed to meeting');
    if (leadScore?.hot_score >= 80) reasons.push('high engagement score');
    // The lead's own words get their OWN sentence. Pushing them into `reasons` produced
    // `Lead shows mentioned "..."`, which was ungrammatical even when the text was clean.
    const reasonText = reasons.length > 0
      ? reasons.slice(0, 2).join(' and ')
      : 'high engagement signals';

    const actualScore = leadScore?.hot_score ? Math.round(Number(leadScore.hot_score)) : null;
const scoreText = actualScore >= 70 ? `${actualScore}% hot score indicates peak engagement.` : '';
const contextText = lastInboundQuote ? ` Their last message: "${lastInboundQuote}".` : '';
reason = `Lead shows ${reasonText}.${scoreText ? ' ' + scoreText : ''}${contextText} Strike while interest is high.`;

    // Build contextual script
    script = buildContextualScript({
      firstName,
      jobTitle,
      city,
      industry,
      escalationReason,
      alertTriggers
    });
    
    icon = Phone;
    confidence = leadScore?.hot_score >= 80 ? 5 : 4;
  } else if (leadScore?.motivation_score > 60) {
    action = 'Prepare for handoff';
    reason = `Motivation score of ${Math.round(leadScore.motivation_score)}% suggests lead is warming up. Monitor for buying signals and be ready to call when they're hot.`;
    icon = Target;
    confidence = 3;
  } else if (analytics?.predictive_indicators?.engagement_trend === 'declining') {
    action = 'Re-engage with new angle';
    reason = 'Engagement is declining. Consider a different messaging approach, new value proposition, or reaching out at a different time.';
    icon = AlertTriangle;
    confidence = 2;
  }

  // Add recommended tone if available
  const recommendedTone = analytics?.predictive_indicators?.recommended_tone || analytics?.content_analysis?.top_performing_tone;
  if (recommendedTone && confidence < 5) {
    reason += ` Recommended tone: ${recommendedTone}.`;
  }

  return { action, reason, script, icon, confidence, signals };
}

// Helper: Build contextual call script
function buildContextualScript({ firstName, jobTitle, city, industry, escalationReason, alertTriggers }) {
  let opener = `Hey ${firstName},`;
  let context = '';
  let ask = `do you have 5 minutes?`;
  let offer = `I can answer your questions and we can see if it makes sense to chat more.`;

  // This function ALSO read `requested_followup_reason`, through its own near-copy of the
  // "isClean" keyword filter, and spliced the result into a script the tenant reads aloud
  // on a live call. Both copies are gone. The signals below (`alertTriggers`) are typed
  // booleans, not free text, so they cannot carry internal wording into the script.
  if (alertTriggers.pricing_inquiry) {
    context = ` I saw your question about pricing`;
    offer = `I can walk you through the details and answer any questions.`;
  } else if (alertTriggers.agreed_to_meeting || alertTriggers.requested_callback) {
    context = ` just following up on scheduling that call`;
    ask = `is now still a good time?`;
    offer = ``;
  } else if (escalationReason === 'buying signal') {
    context = ` I wanted to reach out personally while I had a moment`;
    offer = `I'd love to learn more about what you're looking for.`;
  } else if (industry && city) {
    context = ` reaching out about your ${industry.toLowerCase()} practice in ${city}`;
    offer = `I'd love to hear what you're looking for.`;
  } else if (jobTitle && city) {
    context = ` wanted to connect with you directly`;
    offer = `I'd love to learn more about what you need.`;
  } else {
    context = ` I saw your message and wanted to reach out personally`;
  }

  // No em dashes in tenant-facing copy.
  let script = `${opener}${context}, ${ask}`;
  if (offer) {
    script += ` ${offer}`;
  }

  return script;
}

// Extract topics from analytics
function extractTopics(analytics, messages) {
  const topics = [];
  const seen = new Set();
  
  // From trigger phrases (most relevant)
  const triggers = analytics?.content_analysis?.trigger_phrases || [];
  triggers.forEach(t => {
    const simplified = capitalizeFirst(t);
    if (!seen.has(simplified.toLowerCase()) && simplified.length < 25) {
      seen.add(simplified.toLowerCase());
      topics.push({ label: simplified, emotional: false });
    }
  });
  
  // From emotional appeals (mark as emotional)
  const appeals = analytics?.content_analysis?.emotional_appeals || [];
  appeals.forEach(a => {
    const label = capitalizeFirst(a);
    if (!seen.has(a.toLowerCase())) {
      seen.add(a.toLowerCase());
      topics.push({ label, emotional: true });
    }
  });
  
  // From keywords
  const keywords = analytics?.content_analysis?.keywords || [];
  keywords.forEach(k => {
    if (!seen.has(k.toLowerCase())) {
      seen.add(k.toLowerCase());
      topics.push({ label: capitalizeFirst(k), emotional: false });
    }
  });
  
  // Fallback: extract from messages
  if (topics.length < 3 && messages?.length > 0) {
    const allText = messages.map(m => m.message_body || '').join(' ').toLowerCase();
    const commonTopics = [
      { word: 'pricing', label: 'Pricing' },
      { word: 'timeline', label: 'Timeline' },
      { word: 'schedule', label: 'Schedule' },
      { word: 'budget', label: 'Budget' },
      { word: 'service', label: 'Service' },
      { word: 'help', label: 'Help Needed' }
    ];
    commonTopics.forEach(({ word, label }) => {
      if (allText.includes(word) && !seen.has(word)) {
        seen.add(word);
        topics.push({ label, emotional: false });
      }
    });
  }
  
  return topics.slice(0, 6);
}

// Helper: format time ago
function formatTimeAgo(date, timezone = 'America/New_York') {
  const now = new Date();
  const raw = typeof date === 'string' ? (date.includes('Z') ? date : date + 'Z') : date;
  const d = new Date(raw);
  const diff = (now - d) / 1000;

  // Within last hour - relative
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;

  // Today - show time
  const todayStr = now.toLocaleDateString('en-US', { timeZone: timezone });
  const dStr = d.toLocaleDateString('en-US', { timeZone: timezone });
  if (diff < 86400 && todayStr === dStr) {
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: timezone });
  }

  // Yesterday
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayStr = yesterday.toLocaleDateString('en-US', { timeZone: timezone });
  if (dStr === yesterdayStr) {
    return `Yesterday ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: timezone })}`;
  }

  // This week - show day + time
  if (diff < 604800) {
    return d.toLocaleDateString('en-US', { weekday: 'short', hour: 'numeric', minute: '2-digit', timeZone: timezone });
  }

  // Older - show date
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: timezone });
}

// Helper: capitalize first letter
function capitalizeFirst(str) {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
}

// ============================================================================
// MAIN VISUAL COMPONENTS (unchanged)
// ============================================================================

const StateBar = ({ lead, onToggleAi }) => {
  const navigate = useNavigate();
  // Subtle back button — return to wherever they came from (pipeline, bench tab,
  // search results). Falls back to the app root if they deep-linked straight here.
  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/');
  };
  // Rung labels are the DB's own words so the bar reads as what the lead row holds.
  const states = ['Cold', 'Warm', 'Engaging', 'Hot', 'Escalated'];
  const currentIndex = states.indexOf(lead.state);

  const stateColors = {
    Cold: '#94A3B8',
    Warm: '#F59E0B',
    Engaging: '#3B82F6',
    Hot: '#EF4444',
    Escalated: '#8B5CF6'
  };

  const urgencyColors = {
    low: { bg: 'bg-gray-100', text: 'text-gray-600', dot: '#94A3B8' },
    medium: { bg: 'bg-amber-50', text: 'text-amber-700', dot: '#F59E0B' },
    high: { bg: 'bg-red-50', text: 'text-red-700', dot: '#EF4444' }
  };

  const recommendationStyles = {
    'Call now': { bg: 'bg-green-500', icon: Phone },
    'Hold': { bg: 'bg-gray-400', icon: Pause },
    'AI handling': { bg: 'bg-blue-500', icon: Brain },
    'Needs review': { bg: 'bg-amber-500', icon: AlertTriangle }
  };

  const recStyle = recommendationStyles[lead.recommendation] || recommendationStyles['AI handling'];
  const RecIcon = recStyle.icon;
  const aiEnabled = lead.ai_conversation_enabled !== false;

  return (
    <div className="bg-white border-b border-gray-200 px-6 py-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4">
          <button
            onClick={handleBack}
            title="Back"
            aria-label="Go back"
            className="flex items-center justify-center w-8 h-8 -ml-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors shrink-0"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-xl font-bold text-gray-900">{lead.name}</h1>
          {lead.isWebLead && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-cyan-50 text-cyan-700 ring-1 ring-inset ring-cyan-600/20" title="This lead started a conversation in the chat on your website">
              <MessageCircle className="w-3.5 h-3.5" />
              Web chat
            </span>
          )}
          {lead.isMessengerLead && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-700 ring-1 ring-inset ring-indigo-600/20" title="This lead messaged your Facebook Page">
              <MessageCircle className="w-3.5 h-3.5" />
              Messenger
            </span>
          )}
          {lead.phone && (
            <span className="flex items-center gap-1.5 text-sm text-gray-500 font-medium">
              <Phone className="w-3.5 h-3.5" />
              {lead.phone}
            </span>
          )}
          <div className={clsx(
            "flex items-center gap-2 px-3 py-1.5 rounded-full text-white text-sm font-semibold",
            recStyle.bg
          )}>
            <RecIcon className="w-4 h-4" />
            {lead.recommendation}
          </div>
        </div>
        <div className="flex items-center gap-4">
          {/* AI Conversation Toggle */}
          <button
            onClick={onToggleAi}
            className={clsx(
              "flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-colors duration-200",
              aiEnabled
                ? 'bg-blue-50 border-blue-200'
                : 'bg-gray-100 border-gray-200'
            )}
          >
            <span className={clsx("text-xs font-bold uppercase tracking-wide w-5 text-right", aiEnabled ? 'text-blue-600' : 'text-gray-400')}>
              {aiEnabled ? 'ON' : 'OFF'}
            </span>
            <div
              className={clsx(
                "relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200",
                aiEnabled ? 'bg-blue-600' : 'bg-gray-300'
              )}
            >
              <span
                className={clsx(
                  "inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200",
                  aiEnabled ? 'translate-x-6' : 'translate-x-1'
                )}
              />
            </div>
            <span className={clsx("text-sm font-medium", aiEnabled ? 'text-blue-700' : 'text-gray-500')}>AI</span>
          </button>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <PulseDot color="#22C55E" size={6} />
            <span>Last activity: <span className="font-medium text-gray-700">{lead.lastActivity}</span></span>
          </div>
        </div>
      </div>

      <div className="flex items-start mb-4 overflow-x-auto pb-2">
  {states.map((state, i) => (
          <React.Fragment key={state}>
            <StateStep 
              label={state}
              isActive={i === currentIndex}
              isComplete={i <= currentIndex}
              color={stateColors[state]}
            />
            {i < states.length - 1 && (
              <StateConnector isComplete={i < currentIndex} color={stateColors[states[i + 1]]} />
            )}
          </React.Fragment>
        ))}
      </div>

      <div className="flex items-center gap-6">
        <div className={clsx(
          "flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold",
          urgencyColors[lead.urgency].bg,
          urgencyColors[lead.urgency].text
        )}>
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: urgencyColors[lead.urgency].dot }} />
          {lead.urgency.charAt(0).toUpperCase() + lead.urgency.slice(1)} Urgency
        </div>

        {lead.riskSignals.length > 0 && (
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-500" />
            <div className="flex gap-1.5">
              {lead.riskSignals.map((risk, i) => (
                <span key={i} className="px-2 py-1 bg-amber-50 text-amber-700 text-xs rounded font-medium">
                  {risk}
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="flex items-center gap-2 ml-auto">
          {lead.milestones.map((milestone, i) => (
            <div key={i} className="flex items-center gap-1.5 text-xs text-gray-500">
              <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
              <span>{milestone}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const NarrativeSummary = ({ insights }) => {
  const insightIcons = {
    engagement: { icon: Activity, color: '#3B82F6' },
    motivation: { icon: Flame, color: '#EF4444' },
    tone: { icon: MessageCircle, color: '#22C55E' },
    buying: { icon: Target, color: '#8B5CF6' },
    action: { icon: Zap, color: '#F59E0B' },
    risk: { icon: Shield, color: '#EF4444' }
  };

  return (
    <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-xl p-5">
      <div className="flex items-center gap-2 mb-4">
        <Brain className="w-5 h-5 text-orange-500" />
        <h2 className="text-sm font-bold text-gray-900">AI Analysis</h2>
        <div className="flex items-center gap-1.5 ml-auto text-xs text-gray-500">
          <PulseDot color="#22C55E" size={6} />
          Live
        </div>
      </div>
      
      <div className="space-y-3">
        {insights.map((insight, i) => {
          const config = insightIcons[insight.type] || insightIcons.engagement;
          const Icon = config.icon;
          
          return (
            <div 
              key={i}
              className="flex items-start gap-3 p-3 bg-white rounded-lg border border-gray-100 hover:border-gray-200 transition-colors"
            >
              <div 
                className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${config.color}15` }}
              >
                <Icon className="w-4 h-4" style={{ color: config.color }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-700 leading-relaxed">{insight.text}</p>
                {insight.timestamp && (
                  <p className="text-xs text-gray-400 mt-1">{insight.timestamp}</p>
                )}
              </div>
              {insight.delta && (
                <div className={clsx(
                  "flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded",
                  insight.delta > 0 ? "bg-green-50 text-green-600" : "bg-red-50 text-red-600"
                )}>
                  {insight.delta > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                  {insight.delta > 0 ? '+' : ''}{insight.delta}%
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

  const MessageStream = ({ messages, timezone = 'America/New_York' }) => {
  // Groups are expanded by default: a missing key means "open", so an empty map
  // renders every group open until the user collapses one.
  const [expandedGroups, setExpandedGroups] = useState({});
  const [allExpanded, setAllExpanded] = useState(true);
  const [replayActive, setReplayActive] = useState(false);
  const [visibleMsgs, setVisibleMsgs] = useState(new Set());
  const replayTimers = useRef([]);
  const containerRef = useRef(null);
  const bottomRef = useRef(null);

  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => ({ ...prev, [groupId]: !(prev[groupId] ?? true) }));
  };

  const toggleAll = () => {
    const newState = !allExpanded;
    const newExpanded = {};
    messages.forEach(group => {
      newExpanded[group.id] = newState;
    });
    setExpandedGroups(newExpanded);
    setAllExpanded(newState);
  };

  useEffect(() => {
    const handleReplay = () => {
      // Clear any running timers
      replayTimers.current.forEach(t => clearTimeout(t));
      replayTimers.current = [];

      // Collapse everything and reset visibility. Must be explicit false per
      // group, since an empty map now means "expanded".
      const collapsed = {};
      messages.forEach(group => { collapsed[group.id] = false; });
      setExpandedGroups(collapsed);
      setAllExpanded(false);
      setReplayActive(true);
      setVisibleMsgs(new Set());

      // Scroll into view so the user can see the animation
      if (containerRef.current) {
        containerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }

      // Schedule group expansions and per-message reveals
      let delay = 800;
      messages.forEach((group) => {
        const gDelay = delay;
        const t1 = setTimeout(() => {
          setExpandedGroups(prev => ({ ...prev, [group.id]: true }));
        }, gDelay);
        replayTimers.current.push(t1);

        delay += 500; // wait for group to expand before showing messages

        group.messages.forEach((_, mi) => {
          const mDelay = delay;
          const t2 = setTimeout(() => {
            setVisibleMsgs(prev => new Set([...prev, `${group.id}-${mi}`]));
          }, mDelay);
          replayTimers.current.push(t2);
          delay += 900; // ~1 message per second
        });

        delay += 900; // longer pause between groups
      });
    };

    window.addEventListener('tour-replay-conversation', handleReplay);
    return () => {
      window.removeEventListener('tour-replay-conversation', handleReplay);
      replayTimers.current.forEach(t => clearTimeout(t));
    };
  }, [messages]);

  // Scroll to the latest message as replay progresses
  useEffect(() => {
    if (!replayActive || visibleMsgs.size === 0) return;
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [replayActive, visibleMsgs]);

  if (!messages || messages.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-8 text-center">
        <MessageSquare className="w-8 h-8 text-gray-300 mx-auto mb-2" />
        <p className="text-sm text-gray-500">No messages yet</p>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-gray-400" />
          <h2 className="text-sm font-bold text-gray-900">Conversation</h2>
          <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
            {messages.reduce((acc, g) => acc + g.messages.length, 0)} messages
          </span>
        </div>
        <button
          onClick={toggleAll}
          className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1 px-2 py-1 rounded hover:bg-gray-100 transition-colors"
        >
          {allExpanded ? (
            <>
              <ChevronUp className="w-3 h-3" />
              Collapse
            </>
          ) : (
            <>
              <ChevronDown className="w-3 h-3" />
              Expand
            </>
          )}
        </button>
      </div>

      <div className="divide-y divide-gray-100">
        {messages.map((group) => {
          const isExpanded = expandedGroups[group.id] ?? true;
          
          return (
            <div key={group.id} className="group">
              <button
                onClick={() => toggleGroup(group.id)}
                className="w-full flex items-center gap-3 px-5 py-3 hover:bg-gray-50 transition-colors text-left"
              >
                {isExpanded ? (
                  <ChevronDown className="w-4 h-4 text-gray-400" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                )}
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900">{group.label}</span>
                    {group.highlight && (
                      <span className={clsx(
                        "text-xs px-2 py-0.5 rounded font-medium",
                        group.highlight.type === 'positive' 
                          ? "bg-green-50 text-green-600" 
                          : group.highlight.type === 'negative'
                          ? "bg-red-50 text-red-600"
                          : "bg-blue-50 text-blue-600"
                      )}>
                        {group.highlight.text}
                      </span>
                    )}
                  </div>
                  {!isExpanded && <p className="text-xs text-gray-500 mt-0.5 truncate">{group.preview}</p>}
                </div>
                
                <span className="text-xs text-gray-400">{group.timestamp}</span>
              </button>

              {isExpanded && (
                <div className="px-5 pb-4 space-y-3 bg-gray-50/50">
                  {group.messages.map((msg, i) => {
                    const msgVisible = !replayActive || visibleMsgs.has(`${group.id}-${i}`);
                    if (msg.channel === 'call') {
                      return (
                        <div key={i} className={clsx("flex items-center justify-center gap-2 py-1 transition-all duration-500", msgVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2")}>
                          <div className="flex-1 h-px bg-gray-200" />
                          <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-gray-100 border border-gray-200">
                            <Phone className="w-3 h-3 text-gray-400" />
                            <span className="text-xs text-gray-500 font-medium">{msg.text}</span>
                            <span className="text-xs text-gray-400">· {msg.time}</span>
                          </div>
                          <div className="flex-1 h-px bg-gray-200" />
                        </div>
                      );
                    }
                    return (
                      <div
                        key={i}
                        className={clsx(
                          "flex transition-all duration-500",
                          msg.direction === 'outbound' ? "justify-end" : "justify-start",
                          msgVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"
                        )}
                      >
                        <div className="max-w-[80%]">
                        <div className={clsx(
                          "rounded-2xl relative overflow-hidden",
                          !msg.isFollowUp && "px-4 py-2.5",
                          msg.direction === 'outbound' && ['failed', 'undelivered'].includes(msg.status)
                            ? "bg-gray-300 text-gray-500 rounded-br-md border border-gray-400 border-dashed opacity-60"
                            : msg.direction === 'outbound' && msg.status === 'expired'
                              ? "bg-gray-200 text-gray-400 rounded-br-md border border-gray-300 border-dashed opacity-50"
                            : msg.direction === 'outbound' && msg.businessHoursHold
                              ? "bg-amber-100 text-amber-900 rounded-br-md border border-amber-300 border-dashed"
                              : msg.direction === 'outbound' && msg.isFollowUp
                                ? "bg-orange-50 text-orange-900 rounded-br-md border-2 border-orange-300 border-dashed"
                              : msg.direction === 'outbound' && msg.isManual
                                ? "bg-indigo-600 text-white rounded-br-md ring-2 ring-indigo-300"
                              : msg.direction === 'outbound'
                                ? "bg-orange-500 text-white rounded-br-md"
                                : "bg-white border border-gray-200 text-gray-700 rounded-bl-md"
                        )}>
                          {msg.isFollowUp && (
                            <div className="flex items-center gap-1.5 px-4 py-1.5 bg-orange-100 border-b border-orange-200 text-orange-600 text-xs font-semibold">
                              <RefreshCw className="w-3 h-3" />
                              Automated Follow-up
                            </div>
                          )}
                          <div className={msg.isFollowUp ? "px-4 py-2.5" : ""}>
                          {msg.text && <p className="text-sm leading-relaxed">{msg.text}</p>}
                          {msg.media?.length > 0 && (
                            <MessageAttachments
                              messageId={msg.id}
                              media={msg.media}
                              tone={msg.direction}
                            />
                          )}
                          <div className={clsx(
                            "flex items-center gap-2 mt-1.5 text-xs",
                            msg.direction === 'outbound' && ['failed', 'undelivered'].includes(msg.status)
                              ? "text-gray-400"
                              : msg.direction === 'outbound' && msg.status === 'expired'
                                ? "text-gray-400"
                              : msg.direction === 'outbound' && msg.businessHoursHold
                                ? "text-amber-600"
                                : msg.direction === 'outbound' && msg.isFollowUp ? "text-orange-400"
                                : msg.direction === 'outbound' && msg.isManual ? "text-indigo-200"
                                : msg.direction === 'outbound' ? "text-orange-200" : "text-gray-400"
                          )}>
                            <span>{msg.time}</span>
                            {msg.isFollowUp && (
                              <span className="flex items-center gap-1">
                                <RefreshCw className="w-3 h-3" /> Follow-up
                              </span>
                            )}
                            {msg.isAI && !msg.isFollowUp && (
                              <span className="flex items-center gap-1">
                                <Brain className="w-3 h-3" /> AI
                              </span>
                            )}
                            {msg.isManual && (
                              <span className="flex items-center gap-1 font-semibold">
                                <User className="w-3 h-3" /> Sent manually
                              </span>
                            )}
                            {msg.direction === 'outbound' && msg.businessHoursHold && (
                              <span className="flex items-center gap-1 font-medium">
                                🕐 Queued
                                {msg.scheduledResponseTime && (
                                  <span className="opacity-75">
                                    · sends {new Date(/([zZ]|[+-]\d{2}:?\d{2})$/.test(msg.scheduledResponseTime) ? msg.scheduledResponseTime : msg.scheduledResponseTime + 'Z').toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: timezone })}
                                  </span>
                                )}
                              </span>
                            )}
                            {msg.direction === 'outbound' && msg.status === 'expired' && (
                              <span className="flex items-center gap-1 font-medium text-gray-400">
                                ↩ Expired (not sent)
                              </span>
                            )}
                            {['failed', 'undelivered'].includes(msg.status) && (
                              <span className="flex items-center gap-1 text-red-400 font-medium">
                                ⚠️ Not delivered
                              </span>
                            )}
                          </div>
                          {msg.impact && (
                            <div className={clsx(
                              "absolute -bottom-6 left-0 text-xs font-medium flex items-center gap-1",
                              msg.impact.positive ? "text-green-600" : "text-amber-600"
                            )}>
                              <Sparkles className="w-3 h-3" />
                              {msg.impact.text}
                            </div>
                          )}
                          </div>
                        </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Accuracy disclaimer. Sits at the bottom edge of the conversation card
          rather than outside it, so the parent's layout gap can't detach it
          from the thread it refers to. */}
      <div className="px-5 py-2.5 border-t border-gray-100 bg-gray-50">
        <p className="text-xs text-gray-400">
          AI-generated conversation. Please review for accuracy.
        </p>
      </div>
    </div>
  );
};

const NextStepModule = ({ recommendation }) => {
  const [copied, setCopied] = useState(false);

  const copyScript = () => {
    if (recommendation.script) {
      navigator.clipboard.writeText(recommendation.script);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="bg-gradient-to-br from-orange-50 via-white to-amber-50 border-2 border-orange-200 rounded-xl p-5 relative overflow-hidden">
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-0 right-0 w-32 h-32 bg-orange-200 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-0 left-0 w-24 h-24 bg-amber-200 rounded-full blur-2xl animate-pulse delay-500" />
      </div>

      <div className="relative">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 rounded-lg bg-orange-500 flex items-center justify-center">
            <Zap className="w-4 h-4 text-white" />
          </div>
          <div>
            <h2 className="text-sm font-bold text-gray-900">Next Step</h2>
            <p className="text-xs text-gray-500">AI Recommendation</p>
          </div>
        </div>

        <div className="bg-white rounded-lg p-4 border border-orange-100 mb-4">
          <div className="flex items-center gap-2 mb-2">
            <recommendation.icon className="w-5 h-5 text-orange-500" />
            <span className="font-bold text-gray-900">{recommendation.action}</span>
          </div>
          <p className="text-sm text-gray-600 leading-relaxed">{recommendation.reason}</p>
        </div>

        {recommendation.script && (
          <div className="bg-gray-900 rounded-lg p-4 relative group">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-gray-400 font-medium">Suggested Script</span>
              <button 
                onClick={copyScript}
                className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-white transition-colors"
              >
                <Copy className="w-3.5 h-3.5" />
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <p className="text-sm text-gray-100 leading-relaxed italic">
              "{recommendation.script}"
            </p>
          </div>
        )}

        <div className="flex items-center justify-between mt-4 pt-4 border-t border-orange-100">
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <Brain className="w-3.5 h-3.5" />
            Based on {recommendation.signals} signals
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-500">Confidence</span>
            <div className="flex gap-0.5">
              {[1, 2, 3, 4, 5].map(i => (
                <div 
                  key={i}
                  className={clsx(
                    "w-1.5 h-4 rounded-sm transition-colors",
                    i <= recommendation.confidence ? "bg-orange-500" : "bg-gray-200"
                  )}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const InsightPanel = ({ title, value, trend, trendValue, sparkData, color, icon: Icon }) => (
  <div className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md hover:border-gray-300 transition-all duration-300 group">
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4" style={{ color }} />
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{title}</span>
      </div>
      <div className={clsx(
        "flex items-center gap-1 text-xs font-semibold",
        trend === 'up' ? "text-green-600" : trend === 'down' ? "text-red-600" : "text-gray-400"
      )}>
        {trend === 'up' ? <TrendingUp className="w-3 h-3" /> : 
         trend === 'down' ? <TrendingDown className="w-3 h-3" /> : null}
        {trendValue}
      </div>
    </div>
    <div className="text-2xl font-bold text-gray-900 mb-2" style={{ fontVariantNumeric: 'tabular-nums' }}>
      {value}
    </div>
    <div className="opacity-60 group-hover:opacity-100 transition-opacity">
      <Sparkline data={sparkData} color={color} height={28} />
    </div>
  </div>
);

const ActionsBar = ({ lead, onCall, onSms, onLogCall, onSnooze, onWon, onLost, isPinned, onTogglePin, onCreatePdf, smsOptedOut, onEscalate, isWebLead, isMessengerLead, messengerOptedOut, onPush, showPush }) => {
  // SMS opt-out only blocks the Twilio path. A web-chat or Messenger reply is delivered by
  // its own channel, not by text, so an SMS opt-out never disables either — and the button
  // reads "Message", not "SMS". A Messenger opt-out (leads.opted_out, set by a Messenger
  // STOP) is its own flag — never sms_opted_out, see _shared/messengerGate.ts routeIntent —
  // and blocks only the Messenger path.
  const smsBlocked = smsOptedOut && !isWebLead && !isMessengerLead;
  const messengerBlocked = messengerOptedOut && isMessengerLead;
  const sendBlocked = smsBlocked || messengerBlocked;
  return (
    <div className="fixed bottom-0 left-0 right-0 lg:bottom-6 lg:left-1/2 lg:-translate-x-1/2 lg:right-auto z-50">
      <div className="bg-white border-t lg:border border-gray-200 lg:rounded-2xl shadow-xl px-2 py-2 overflow-x-auto">
        <div className="flex items-center gap-1 min-w-max justify-center">
          {/* Call Lead */}
          {lead.phone && (
            <button
              onClick={onCall}
              className="flex items-center gap-2 px-3 lg:px-4 py-2.5 rounded-xl text-sm font-semibold bg-green-500 text-white hover:bg-green-600 shadow-sm transition-all"
            >
              <Phone className="w-4 h-4" />
              <span className="hidden sm:inline">Call</span>
            </button>
          )}

          {/* Send SMS / Web chat / Messenger reply */}
          <button
            onClick={sendBlocked ? undefined : onSms}
            disabled={sendBlocked}
            className={`flex items-center gap-2 px-3 lg:px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${
              sendBlocked
                ? 'text-gray-400 cursor-not-allowed opacity-50'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
            title={
              smsBlocked ? 'This lead has opted out of SMS'
              : messengerBlocked ? 'This lead has opted out on Messenger'
              : isWebLead ? 'Reply in web chat'
              : isMessengerLead ? 'Reply on Messenger'
              : 'Send SMS'
            }
          >
            <Send className="w-4 h-4" />
            <span className="hidden sm:inline">{sendBlocked ? 'Opted Out' : (isMessengerLead ? 'Messenger' : isWebLead ? 'Message' : 'SMS')}</span>
          </button>

          {/* Log Call */}
          <button
            onClick={onLogCall}
            className="flex items-center gap-2 px-3 lg:px-4 py-2.5 rounded-xl text-sm font-semibold text-gray-700 hover:bg-gray-100 transition-all"
          >
            <MessageSquare className="w-4 h-4" />
            <span className="hidden sm:inline">Log</span>
          </button>

          {/* Pin to Dashboard */}
          <button
            onClick={onTogglePin}
            className={`flex items-center gap-2 px-3 lg:px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${
              isPinned
                ? 'text-amber-600 bg-amber-50 hover:bg-amber-100'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
            title={isPinned ? 'Remove from Dashboard' : 'Pin to Dashboard'}
          >
            {isPinned ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />}
            <span className="hidden sm:inline">{isPinned ? 'Unpin' : 'Pin'}</span>
          </button>

          {/* Create PDF */}
          <button
            onClick={onCreatePdf}
            className="flex items-center gap-2 px-3 lg:px-4 py-2.5 rounded-xl text-sm font-semibold text-gray-700 hover:bg-gray-100 transition-all"
            title="Export conversation as PDF"
          >
            <FileText className="w-4 h-4" />
            <span className="hidden sm:inline">PDF</span>
          </button>

          {/* Escalate */}
          <button
            onClick={onEscalate}
            className="flex items-center gap-2 px-3 lg:px-4 py-2.5 rounded-xl text-sm font-semibold text-amber-600 hover:bg-amber-50 transition-all"
            title="Escalate (flag an issue with this lead)"
          >
            <AlertTriangle className="w-4 h-4" />
            <span className="hidden sm:inline">Escalate</span>
          </button>

          {/* Push to CRM — only when the tenant has at least one connected integration */}
          {showPush && (
            <button
              onClick={onPush}
              className="flex items-center gap-2 px-3 lg:px-4 py-2.5 rounded-xl text-sm font-semibold text-gray-700 hover:bg-gray-100 transition-all"
              title="Push this lead to your connected CRM"
            >
              <ExternalLink className="w-4 h-4" />
              <span className="hidden sm:inline">Push</span>
            </button>
          )}

          <div className="w-px h-8 bg-gray-200 mx-1" />

          {/* Snooze. Once a lead is snoozed this becomes a plain state indicator, not a
              toggle: there is no manual wake yet, so offering one here would lie. The
              snooze releases when the lead texts back. Snoozing is one-way from the UI
              until the wake control is built — surface the state, don't hide it. */}
          {isSnoozed(lead) ? (
            <span
              title="SurFox is paused on this lead. The snooze lifts when the lead texts back."
              className="flex items-center gap-2 px-3 lg:px-4 py-2.5 rounded-xl text-sm font-semibold text-violet-700 bg-violet-50"
            >
              <Clock className="w-4 h-4" />
              <span className="hidden sm:inline">Snoozed</span>
            </span>
          ) : (
            <button
              onClick={onSnooze}
              className="flex items-center gap-2 px-3 lg:px-4 py-2.5 rounded-xl text-sm font-semibold text-gray-700 hover:bg-gray-100 transition-all"
            >
              <Clock className="w-4 h-4" />
              <span className="hidden sm:inline">Snooze</span>
            </button>
          )}

          {/* Won */}
          <button
            onClick={onWon}
            className="flex items-center gap-2 px-3 lg:px-4 py-2.5 rounded-xl text-sm font-semibold text-green-600 hover:bg-green-50 transition-all"
          >
            <CheckCircle2 className="w-4 h-4" />
            <span className="hidden sm:inline">Won</span>
          </button>

          {/* Lost */}
          <button
            onClick={onLost}
            className="flex items-center gap-2 px-3 lg:px-4 py-2.5 rounded-xl text-sm font-semibold text-red-600 hover:bg-red-50 transition-all"
          >
            <XCircle className="w-4 h-4" />
            <span className="hidden sm:inline">Lost</span>
          </button>
        </div>
      </div>
    </div>
  );
};

// Friendly copy for the machine-readable skip reasons the edge function returns.
const PUSH_REASON_TEXT = {
  fub_person_id_missing: 'This lead did not come from Follow Up Boss, so there is no contact to update',
  odoo_lead_id_missing:  'This lead did not come from Odoo, so there is no record to update',
  fub_missing_api_key:   'Follow Up Boss is missing its API key in Settings',
  no_delivery_target:    'This integration has no delivery URL configured',
  recently_pushed:       'Already pushed in the last few minutes',
  not_found_or_disabled: 'This integration is unavailable',
};

// Push a lead into the tenant's connected CRM(s). Lists destinations with
// per-lead eligibility (ineligible ones shown disabled with the reason up
// front, so nothing silently fails), then enqueues the chosen ones through the
// same delivery machinery the automatic hot-lead push uses.
const PushToCrmModal = ({ isOpen, onClose, leadId, leadName }) => {
  const [loading, setLoading] = useState(false);
  const [destinations, setDestinations] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [pushing, setPushing] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    // Reset every open so a reopen never shows a prior lead's state.
    setResults(null); setError(null); setDestinations([]); setSelected(new Set());
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data, error: invokeErr } = await supabase.functions.invoke('push-lead-to-integrations', {
        body: { lead_id: leadId, list: true },
      });
      if (cancelled) return;
      if (invokeErr || data?.error) {
        setError('Could not load your integrations. Please try again.');
      } else {
        const dests = data?.destinations || [];
        setDestinations(dests);
        // Preselect every eligible destination.
        setSelected(new Set(dests.filter(d => d.eligible).map(d => d.id)));
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [isOpen, leadId]);

  if (!isOpen) return null;

  const toggle = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handlePush = async () => {
    setPushing(true);
    setError(null);
    const { data, error: invokeErr } = await supabase.functions.invoke('push-lead-to-integrations', {
      body: { lead_id: leadId, connection_ids: Array.from(selected) },
    });
    setPushing(false);
    if (invokeErr || data?.error) {
      setError('The push could not be completed. Please try again.');
      return;
    }
    setResults(data?.results || []);
  };

  const eligibleCount = destinations.filter(d => d.eligible).length;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-gray-200">
          <h3 className="text-base font-semibold text-gray-900">Push to CRM</h3>
          <p className="text-sm text-gray-500 mt-0.5">
            Send {leadName || 'this lead'} to your connected integrations.
          </p>
        </div>

        <div className="px-5 py-4 max-h-80 overflow-y-auto">
          {loading ? (
            <div className="text-sm text-gray-500 py-6 text-center">Loading integrations…</div>
          ) : error && !results ? (
            <div className="text-sm text-red-600 py-4">{error}</div>
          ) : results ? (
            <div className="space-y-2">
              {results.map((r, i) => {
                const dest = destinations.find(d => d.id === r.connection_id);
                const name = r.integration_name || dest?.integration_name || 'Integration';
                // Delivery outcome (only present on enqueued rows); skipped rows
                // fall through to the reason branch.
                const delivery = r.status === 'enqueued' ? (r.delivery || 'queued') : 'skipped';
                let Icon, iconClass, text;
                if (delivery === 'delivered') {
                  Icon = CheckCircle2; iconClass = 'text-green-600'; text = ' — delivered';
                } else if (delivery === 'failed') {
                  Icon = XCircle; iconClass = 'text-red-500';
                  text = `${r.retrying ? ' — could not deliver (retrying): ' : ' — failed: '}${r.detail || 'Delivery failed.'}`;
                } else if (delivery === 'queued') {
                  Icon = Clock; iconClass = 'text-gray-400'; text = ' — queued, sending shortly';
                } else {
                  Icon = XCircle; iconClass = 'text-gray-400';
                  text = ` — skipped: ${PUSH_REASON_TEXT[r.reason] || r.reason}`;
                }
                return (
                  <div key={i} className="flex items-start gap-2 text-sm">
                    <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${iconClass}`} />
                    <div>
                      <span className="font-medium text-gray-900">{name}</span>
                      <span className="text-gray-500">{text}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : destinations.length === 0 ? (
            <div className="text-sm text-gray-500 py-4">No connected integrations found.</div>
          ) : (
            <div className="space-y-2">
              {destinations.map(d => (
                <label
                  key={d.id}
                  className={`flex items-start gap-3 p-3 rounded-xl border transition-colors ${
                    d.eligible
                      ? 'border-gray-200 hover:bg-gray-50 cursor-pointer'
                      : 'border-gray-100 bg-gray-50 cursor-not-allowed'
                  }`}
                >
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 rounded border-gray-300"
                    disabled={!d.eligible}
                    checked={selected.has(d.id)}
                    onChange={() => toggle(d.id)}
                  />
                  <div className="min-w-0">
                    <div className={`text-sm font-medium ${d.eligible ? 'text-gray-900' : 'text-gray-400'}`}>
                      {d.integration_name}
                    </div>
                    {!d.eligible && (
                      <div className="text-xs text-gray-500 mt-0.5">
                        {PUSH_REASON_TEXT[d.reason] || d.reason}
                      </div>
                    )}
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-gray-200 flex justify-end gap-2">
          {results ? (
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-xl text-sm font-semibold bg-gray-900 text-white hover:bg-gray-800 transition-all"
            >
              Done
            </button>
          ) : (
            <>
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-xl text-sm font-semibold text-gray-700 hover:bg-gray-100 transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handlePush}
                disabled={pushing || loading || eligibleCount === 0 || selected.size === 0}
                className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
                  pushing || loading || eligibleCount === 0 || selected.size === 0
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-900 text-white hover:bg-gray-800'
                }`}
              >
                {pushing ? 'Pushing…' : `Push${selected.size > 0 ? ` (${selected.size})` : ''}`}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const TopicMap = ({ topics }) => (
  <div className="bg-white border border-gray-200 rounded-xl p-4">
    <div className="flex items-center gap-2 mb-3">
      <Target className="w-4 h-4 text-gray-400" />
      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Topics Discussed</span>
    </div>
    <div className="flex flex-wrap gap-2">
      {topics.length === 0 ? (
        <span className="text-sm text-gray-400">No topics detected yet</span>
      ) : (
        topics.map((topic, i) => (
          <span
            key={i}
            className={clsx(
              "px-3 py-1.5 rounded-lg text-sm font-medium transition-all hover:scale-105",
              topic.emotional
                ? "bg-purple-100 text-purple-700 border border-purple-200"
                : "bg-gray-100 text-gray-700"
            )}
          >
            {topic.emotional && <Flame className="w-3 h-3 inline mr-1" />}
            {topic.label}
          </span>
        ))
      )}
    </div>
  </div>
);

// Collapsible card surfacing everything else we know about the lead.
// Customer-facing: pulls enrichment (custom_fields) plus a curated set of
// standard columns, formats them cleanly, and hides system/noise fields.
const LeadInfoCard = ({ lead, timezone = 'America/New_York' }) => {
  const [expanded, setExpanded] = useState(false);
  if (!lead) return null;

  const formatValue = (val) => {
    if (val === null || val === undefined) return null;
    if (typeof val === 'boolean') return val ? 'Yes' : 'No';
    if (Array.isArray(val)) {
      const joined = val.filter(v => v !== null && v !== undefined && v !== '').join(', ');
      return joined || null;
    }
    if (typeof val === 'object') {
      const joined = Object.values(val).filter(Boolean).join(', ');
      return joined || null;
    }
    const str = String(val).trim();
    return str === '' ? null : str;
  };

  const formatDate = (val) => {
    if (!val) return null;
    const raw = typeof val === 'string'
      ? (/([zZ]|[+-]\d{2}:?\d{2})$/.test(val) ? val : val.replace(' ', 'T') + 'Z')
      : val;
    const d = new Date(raw);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: timezone });
  };

  // Curated standard fields — labels are human-facing.
  const standard = [
    { label: 'Email', value: formatValue(lead.email) },
    { label: 'Company', value: formatValue(lead.company) },
    { label: 'Source', value: formatValue(lead.lead_source || lead.source) },
    { label: 'Added', value: formatDate(lead.created_at) },
  ].filter(f => f.value);

  // Enrichment data — keys are already human-readable ("Job Title", "Person City").
  // Skip anything that just duplicates the header (name / phone) or looks internal.
  const skipKeys = new Set(['name', 'first_name', 'last_name', 'full_name', 'phone', 'phone_number', 'mobile', 'email']);
  const custom = Object.entries(lead.custom_fields || {})
    .filter(([k]) => !skipKeys.has(k.toLowerCase().replace(/\s+/g, '_')) && !k.startsWith('_'))
    .map(([k, v]) => ({ label: k, value: formatValue(v) }))
    .filter(f => f.value);

  const rows = [...standard, ...custom];
  if (rows.length === 0) return null;

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 px-4 py-3 hover:bg-gray-50 transition-colors text-left"
        aria-expanded={expanded}
      >
        <User className="w-4 h-4 text-gray-400" />
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Lead Details</span>
        <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{rows.length}</span>
        {expanded
          ? <ChevronUp className="w-4 h-4 text-gray-400 ml-auto" />
          : <ChevronDown className="w-4 h-4 text-gray-400 ml-auto" />}
      </button>

      {expanded && (
        <dl className="px-4 pb-3 divide-y divide-gray-100 border-t border-gray-100">
          {rows.map((row, i) => (
            <div key={i} className="flex items-start justify-between gap-4 py-2.5">
              <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide flex-shrink-0 max-w-[45%] leading-relaxed">
                {row.label}
              </dt>
              <dd className="text-sm text-gray-900 text-right break-words min-w-0">
                {row.value}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
};

const UnansweredQuestionsCard = ({ questions, appointment, timezone = 'America/New_York' }) => {
  if (questions.length === 0) return null;

  const normalizeTs = (ts) => {
    if (!ts) return null;
    // Postgres returns "2026-02-27 22:36:42.408+00" — replace space with T, normalize +00 → Z
    return ts.replace(' ', 'T').replace(/\+00(:00)?$/, 'Z');
  };

  const formatDateTime = (timestamp) => {
    const date = new Date(normalizeTs(timestamp) ?? '');
    if (isNaN(date.getTime())) return 'Unknown';
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: timezone
    });
  };

  const formatAppointmentTime = (timestamp) => {
    const date = new Date(normalizeTs(timestamp) ?? '');
    if (isNaN(date.getTime())) return 'Unknown';
    return date.toLocaleString('en-US', {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: timezone,
      timeZoneName: 'short'
    });
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4">
      <div className="flex items-center gap-2 mb-3">
        <ClipboardList className="w-4 h-4 text-amber-500" />
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
          {questions.length} question{questions.length > 1 ? 's' : ''} couldn't be answered from knowledge base
        </span>
      </div>

      <div className="space-y-3">
        {questions.map((q, i) => (
          <div key={q.id || i} className="border-l-2 border-amber-200 pl-3">
            <div className="flex items-start gap-2">
              <span className="text-amber-600">📋</span>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">"{q.question_text}"</p>
                <p className="text-xs text-gray-500 mt-1">Asked {formatDateTime(q.asked_at)}</p>
              </div>
            </div>
          </div>
        ))}

        {appointment && (
          <div className="mt-4 pt-3 border-t border-gray-200">
            <div className="flex items-start gap-2 text-sm">
              <ArrowRight className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-gray-700 font-medium">AI escalated to scheduling mode</p>
                <p className="text-green-700 font-semibold mt-1">
                  Call scheduled: {formatAppointmentTime(appointment.scheduled_time)}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// SMS Modal
const SmsModal = ({ isOpen, onClose, onSend, message, setMessage, sending, leadName, aiEnabled, isWebLead, isMessengerLead }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-6 w-full max-w-md mx-4 shadow-2xl">
        <h3 className="text-lg font-bold text-gray-900 mb-4">
          {isWebLead ? `Reply to ${leadName} in web chat` : isMessengerLead ? `Send Messenger message to ${leadName}` : `Send SMS to ${leadName}`}
        </h3>
        {aiEnabled && (
          <div className="flex items-start gap-2.5 mb-4 p-3 rounded-xl bg-orange-50 border border-orange-100">
            <Sparkles className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-gray-600 leading-relaxed">
              Sending a manual message pauses the AI for this lead so you two don't talk over each other. Just remember to flip the AI back on when you want it to take over again.
            </p>
          </div>
        )}
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Type your message..."
          className="w-full h-32 p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
          disabled={sending}
        />
        <div className="flex gap-3 mt-4">
          <button
            onClick={onClose}
            disabled={sending}
            className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 transition-all"
          >
            Cancel
          </button>
          <button
            onClick={onSend}
            disabled={!message.trim() || sending}
            className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold text-white bg-orange-500 hover:bg-orange-600 disabled:opacity-50 transition-all flex items-center justify-center gap-2"
          >
            {sending ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                Send
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// Log Call Modal
const LogCallModal = ({ isOpen, onClose, onLog, saving }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-6 w-full max-w-sm mx-4 shadow-2xl">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Log Call Outcome</h3>
        <div className="space-y-2">
          <button
            onClick={() => onLog('connected')}
            disabled={saving}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-gray-700 hover:bg-green-50 transition-all"
          >
            <CheckCircle2 className="w-5 h-5 text-green-500" />
            Connected
          </button>
          <button
            onClick={() => onLog('voicemail')}
            disabled={saving}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-gray-700 hover:bg-blue-50 transition-all"
          >
            <MessageSquare className="w-5 h-5 text-blue-500" />
            Left Voicemail
          </button>
          <button
            onClick={() => onLog('no_answer')}
            disabled={saving}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-all"
          >
            <Phone className="w-5 h-5 text-gray-500" />
            No Answer
          </button>
          <button
            onClick={() => onLog('wrong_number')}
            disabled={saving}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-gray-700 hover:bg-red-50 transition-all"
          >
            <XCircle className="w-5 h-5 text-red-500" />
            Wrong Number
          </button>
        </div>
        <button
          onClick={onClose}
          disabled={saving}
          className="w-full mt-4 px-4 py-2.5 rounded-xl text-sm font-semibold text-gray-500 hover:bg-gray-100 transition-all"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

// Snooze Modal
// A snooze has NO expiry. There is deliberately no duration picker: every option in
// one ("tomorrow", "in 3 days") is a promise to wake the lead without the rep asking,
// which is the opposite of what pressing Snooze means. The lead stays put until it
// texts back, which is currently the ONLY release — a manual wake control is not
// built yet, so this copy must not promise one.
const SnoozeModal = ({ isOpen, onClose, onSnooze, saving }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-6 w-full max-w-sm mx-4 shadow-2xl">
        <h3 className="text-lg font-bold text-gray-900 mb-2">Snooze Lead</h3>
        <p className="text-sm text-gray-500 mb-5">
          SurFox stops messaging this lead and takes them off your board. There's no
          timer — they stay snoozed until the lead texts back.
        </p>
        <button
          onClick={() => onSnooze()}
          disabled={saving}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold text-white bg-gray-900 hover:bg-gray-800 disabled:opacity-50 transition-all"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Clock className="w-4 h-4" />}
          Snooze this lead
        </button>
        <button
          onClick={onClose}
          disabled={saving}
          className="w-full mt-3 px-4 py-2.5 rounded-xl text-sm font-semibold text-gray-500 hover:bg-gray-100 transition-all"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

// Won Modal - capture deal value
const WonModal = ({ isOpen, onClose, onSubmit, saving, existingValue }) => {
  const [dealValue, setDealValue] = useState(existingValue || '');
  
  if (!isOpen) return null;

  const handleSubmit = () => {
    const value = parseFloat(dealValue);
    if (!value || value <= 0) {
      alert('Please enter a valid deal value');
      return;
    }
    onSubmit(value);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-6 w-full max-w-sm mx-4 shadow-2xl">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
            <CheckCircle2 className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">Mark as Won</h3>
            <p className="text-sm text-gray-500">Enter the deal value</p>
          </div>
        </div>
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Deal Value <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">$</span>
            <input
              type="number"
              value={dealValue}
              onChange={(e) => setDealValue(e.target.value)}
              placeholder="0"
              className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent text-lg font-semibold"
              disabled={saving}
              autoFocus
            />
          </div>
        </div>

        <div className="flex gap-3">
          <button
            onClick={onClose}
            disabled={saving}
            className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 transition-all"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!dealValue || saving}
            className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold text-white bg-green-500 hover:bg-green-600 disabled:opacity-50 transition-all flex items-center justify-center gap-2"
          >
            {saving ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <CheckCircle2 className="w-4 h-4" />
                Confirm Won
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// CALL MODAL
// ============================================================================

const CallModal = ({ isOpen, lead, isReady, twilioError, callStatus, callDuration, isMuted, makeCall, hangup, toggleMute, formatDuration, onDisposition, onSkip }) => {
  const callStartedRef = useRef(false);
  const [showOutcome, setShowOutcome] = useState(false);
  const [saving, setSaving] = useState(false);

  // Auto-start call when device is ready
  useEffect(() => {
    if (isReady && callStatus === 'idle' && !callStartedRef.current && lead?.phone) {
      callStartedRef.current = true;
      makeCall(lead.phone, { id: lead.id });
    }
  }, [isReady, callStatus, lead, makeCall]);

  // Show outcome UI after call ends
  useEffect(() => {
    if (callStatus === 'ended') setShowOutcome(true);
  }, [callStatus]);

  if (!isOpen) return null;

  const handleDisposition = async (disp) => {
    setSaving(true);
    await onDisposition(disp);
    setSaving(false);
  };

  const statusLabel = () => {
    if (!isReady && callStatus === 'idle') return 'Initializing dialer...';
    if (callStatus === 'idle') return 'Ready';
    if (callStatus === 'connecting') return 'Connecting...';
    if (callStatus === 'ringing') return 'Ringing...';
    if (callStatus === 'in-progress') return formatDuration(callDuration);
    if (callStatus === 'ended') return `Ended • ${formatDuration(callDuration)}`;
    return '';
  };

  const statusColor =
    callStatus === 'in-progress' ? 'bg-green-500'
    : callStatus === 'connecting' || callStatus === 'ringing' ? 'bg-amber-400'
    : callStatus === 'ended' ? 'bg-red-500'
    : 'bg-gray-400';

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden">
        {/* Header */}
        <div className="px-6 pt-6 pb-4 text-center">
          <h2 className="text-xl font-bold text-gray-900">{lead?.name || 'Lead'}</h2>
          <p className="text-sm text-gray-500 mt-1">{lead?.phone}</p>
          <div className="flex items-center justify-center gap-2 mt-3">
            <span className={`w-2 h-2 rounded-full ${statusColor}`} />
            <span className="text-sm font-semibold text-gray-700">{statusLabel()}</span>
          </div>
          {twilioError && (
            <div className="mt-2 px-3 py-2 bg-red-50 rounded-lg border border-red-200">
              <p className="text-xs text-red-600">{twilioError}</p>
            </div>
          )}
        </div>

        {/* Pulse animation during connecting/ringing */}
        {(callStatus === 'connecting' || callStatus === 'ringing') && (
          <div className="flex justify-center py-4">
            <div className="relative w-16 h-16">
              <span className="absolute inset-0 rounded-full border-2 border-orange-400 animate-ping opacity-50" />
              <span className="absolute inset-0 rounded-full border-2 border-orange-400 animate-ping opacity-30" style={{ animationDelay: '0.5s' }} />
              <span className="relative flex items-center justify-center w-16 h-16 rounded-full bg-orange-50">
                <Phone className="w-6 h-6 text-orange-500" />
              </span>
            </div>
          </div>
        )}

        {/* Cancel button during connecting/ringing */}
        {(callStatus === 'connecting' || callStatus === 'ringing') && (
          <div className="flex justify-center pb-5">
            <button onClick={hangup} className="px-6 py-2.5 rounded-xl bg-red-500 text-white text-sm font-semibold hover:bg-red-600 transition-all">
              Cancel
            </button>
          </div>
        )}

        {/* Call controls (in-progress) */}
        {callStatus === 'in-progress' && (
          <div className="flex items-center justify-center gap-6 py-5">
            <button
              onClick={toggleMute}
              className={`flex flex-col items-center gap-1 px-5 py-3 rounded-xl border transition-all ${isMuted ? 'bg-orange-50 border-orange-300 text-orange-600' : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'}`}
            >
              {isMuted ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
              <span className="text-xs font-medium">{isMuted ? 'Unmute' : 'Mute'}</span>
            </button>
            <button
              onClick={hangup}
              className="flex flex-col items-center gap-1 px-5 py-3 rounded-xl bg-red-500 text-white hover:bg-red-600 transition-all"
            >
              <Phone className="w-5 h-5 rotate-[135deg]" />
              <span className="text-xs font-medium">End Call</span>
            </button>
          </div>
        )}

        {/* Post-call outcome */}
        {showOutcome && (
          <div className="border-t border-gray-100 px-6 py-4">
            <p className="text-sm font-semibold text-gray-700 mb-3 text-center">Call Outcome</p>
            <div className="flex gap-2 mb-3">
              <button onClick={() => handleDisposition('won')} disabled={saving}
                className="flex-1 py-2.5 rounded-xl bg-green-500 text-white text-sm font-semibold hover:bg-green-600 disabled:opacity-50 transition-all">
                Won
              </button>
              <button onClick={() => handleDisposition('lost')} disabled={saving}
                className="flex-1 py-2.5 rounded-xl bg-red-500 text-white text-sm font-semibold hover:bg-red-600 disabled:opacity-50 transition-all">
                Lost
              </button>
            </div>
            <p className="text-xs text-gray-400 mb-2 text-center">Or log call as:</p>
            <div className="grid grid-cols-2 gap-2">
              {[
                { value: 'connected', label: 'Connected', icon: CheckCircle2, color: 'text-green-500' },
                { value: 'voicemail', label: 'Left Voicemail', icon: MessageSquare, color: 'text-blue-500' },
                { value: 'no_answer', label: 'No Answer', icon: Phone, color: 'text-gray-500' },
                { value: 'wrong_number', label: 'Wrong Number', icon: XCircle, color: 'text-red-500' },
              ].map(opt => (
                <button key={opt.value} onClick={() => handleDisposition(opt.value)} disabled={saving}
                  className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-gray-50 border border-gray-200 text-xs font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50 transition-all">
                  <opt.icon className={`w-4 h-4 ${opt.color}`} />
                  {opt.label}
                </button>
              ))}
            </div>
            <button onClick={onSkip} disabled={saving}
              className="w-full mt-3 py-2 text-xs text-gray-400 hover:text-gray-600 transition-all">
              Skip
            </button>
          </div>
        )}

        {/* Close button on error */}
        {callStatus === 'idle' && twilioError && (
          <div className="px-6 pb-5">
            <button onClick={onSkip}
              className="w-full py-2.5 rounded-xl bg-gray-100 text-gray-600 text-sm font-medium hover:bg-gray-200 transition-all">
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT - WIRED TO SUPABASE
// ============================================================================

const LeadDetail = () => {
  const { id: leadId } = useParams();
  const { session } = useAuth();
  const tenantId = null;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [leadData, setLeadData] = useState(null);
  const [leadScore, setLeadScore] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [messages, setMessages] = useState([]);
  const [scoreHistory, setScoreHistory] = useState([]);
  const [unansweredQuestions, setUnansweredQuestions] = useState([]);
  const [scheduledAppointment, setScheduledAppointment] = useState(null);
  const [timezone, setTimezone] = useState('America/New_York');
  const TZ_ABBREVIATION_MAP = {
    'EST': 'America/New_York',    'EDT': 'America/New_York',
    'CST': 'America/Chicago',     'CDT': 'America/Chicago',
    'MST': 'America/Denver',      'MDT': 'America/Denver',
    'PST': 'America/Los_Angeles', 'PDT': 'America/Los_Angeles',
    'AKST': 'America/Anchorage',  'HST': 'Pacific/Honolulu'
  };
  
// Twilio calling
  const twilioCall = useTwilioCall({ session, supabase });
  const [showCallModal, setShowCallModal] = useState(false);

// Action bar state
  const [showSmsModal, setShowSmsModal] = useState(false);
  const [showLogCallModal, setShowLogCallModal] = useState(false);
  const [showSnoozeModal, setShowSnoozeModal] = useState(false);
  const [showWonModal, setShowWonModal] = useState(false);
  const [showEscalationModal, setShowEscalationModal] = useState(false);
  const [showPushModal, setShowPushModal] = useState(false);
  const [hasIntegrations, setHasIntegrations] = useState(false);
  const [smsMessage, setSmsMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [savingAction, setSavingAction] = useState(false);

  // Gate the Push button on the tenant actually having a connected integration.
  // Count-only projection — never pulls the credentials column into the browser.
  useEffect(() => {
    const tid = leadData?.tenant_id;
    if (!tid) { setHasIntegrations(false); return; }
    let cancelled = false;
    (async () => {
      const { count, error } = await supabase
        .from('integration_connections')
        .select('id', { count: 'exact', head: true })
        .eq('tenant_id', tid)
        .eq('enabled', true);
      if (!cancelled) setHasIntegrations(!error && (count || 0) > 0);
    })();
    return () => { cancelled = true; };
  }, [leadData?.tenant_id]);

  useEffect(() => {
    if (!leadId) return;

    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        // 1. Fetch lead — sequential, since the page header needs this before it can render.
        const { data: lead, error: leadError } = await supabase
          .from('leads')
          .select('*')
          .eq('id', leadId)
          .single();

        if (cancelled) return;
        if (leadError) throw leadError;

        setLeadData(lead);
        setLoading(false); // Page can render now; remaining sections fill in as their queries resolve.

        const effectiveTenantId = tenantId || lead?.tenant_id;

        // 2-7. Aux queries fire in parallel and each updates its own state slot independently.
        // No outer await — the render path doesn't block on these.
        Promise.all([
          effectiveTenantId
            ? supabase
                .from('platform_settings')
                .select('value')
                .eq('tenant_id', effectiveTenantId)
                .eq('key', 'timezone')
                .maybeSingle()
                .then(({ data }) => {
                  if (cancelled) return;
                  if (data?.value) setTimezone(TZ_ABBREVIATION_MAP[data.value] || data.value);
                })
            : null,

          supabase
            .from('lead_scores')
            .select('*')
            .eq('lead_id', leadId)
            .order('created_at', { ascending: true })
            .limit(20)
            .then(({ data, error }) => {
              if (cancelled || error || !data?.length) return;
              setLeadScore(data[data.length - 1]);
              setScoreHistory(data);
            }),

          supabase
            .from('conversation_analytics')
            .select('*')
            .eq('lead_id', leadId)
            .order('sales_metric_date', { ascending: false })
            .limit(1)
            .then(({ data, error }) => {
              if (cancelled || error || !data?.length) return;
              setAnalytics(data[0]);
            }),

          supabase
            .from('messages')
            .select('*')
            .eq('lead_id', leadId)
            .order('timestamp', { ascending: true })
            .then(({ data, error }) => {
              if (cancelled || error) return;
              setMessages(data || []);
            }),

          supabase
            .from('unanswered_questions')
            .select('*')
            .eq('lead_id', leadId)
            .eq('resolved', false)
            .order('asked_at', { ascending: true })
            .then(({ data, error }) => {
              if (cancelled || error) return;
              setUnansweredQuestions(data || []);
            }),

          supabase
            .from('appointments')
            .select('*')
            .eq('lead_id', leadId)
            .eq('status', 'scheduled')
            .order('scheduled_time', { ascending: true })
            .limit(1)
            .maybeSingle()
            .then(({ data, error }) => {
              if (cancelled || error || !data) return;
              setScheduledAppointment(data);
            }),
        ]).catch(err => {
          // Aux-fetch errors don't block the page; sections without data fall back to their
          // existing empty/null states. Log for diagnostics.
          if (!cancelled) console.error('LeadDetail aux fetch error:', err);
        });

      } catch (err) {
        if (cancelled) return;
        console.error('Error fetching lead data:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchData();
    return () => { cancelled = true; };
  }, [leadId, tenantId]);

  // Transform data for UI
  const lead = useMemo(() => {
    if (!leadData) return null;

    const lastMsg = messages[messages.length - 1];
    const lastActivity = lastMsg ? formatTimeAgo(lastMsg.timestamp, timezone) : 'No activity';

    return {
      id: leadData.id,
      name: leadData.name || 'Unknown Lead',
      phone: leadData.phone || '',
      state: mapStatusToState(leadData.status),
      recommendation: generateRecommendation(leadData, leadScore, analytics),
      urgency: calculateUrgency(leadScore, analytics),
      lastActivity,
      riskSignals: extractRiskSignals(analytics, leadScore),
      milestones: extractMilestones(leadData, leadScore, messages),
      aiPaused: leadData.ai_conversation_enabled === false,
      ai_conversation_enabled: leadData.ai_conversation_enabled,
      // Carried so ActionsBar can flip Snooze → Wake. This object is an explicit
      // projection, not a spread of leadData, so anything not listed here is invisible
      // to every component below.
      snoozed_until: leadData.snoozed_until,
      // Web chat lead (session_id is set only by the website chat widget). Drives the
      // "Web chat" badge in the header; listed here because this projection is explicit.
      isWebLead: !!leadData.session_id,
      // Messenger lead (leads.channel='messenger', identified by external_id/PSID, never a
      // phone or session_id). Drives the "Messenger" badge, same idea as isWebLead above.
      isMessengerLead: leadData.channel === 'messenger',
      motivationScore: Math.round(leadScore?.motivation_score || 50),
      hesitationScore: Math.round(leadScore?.hesitation_score || 50),
      responseTime: leadScore?.avg_reply_delay ? Math.round(leadScore.avg_reply_delay / 60 * 10) / 10 : 0,
      conversationDepth: messages.length
    };
  }, [leadData, leadScore, analytics, messages]);

  const insights = useMemo(() =>
    generateInsights(analytics, leadScore, leadData, messages),
    [analytics, leadScore, leadData, messages]
  );

  const messageGroups = useMemo(() =>
    groupMessages(messages, timezone),
    [messages, timezone]
  );

  const nextStep = useMemo(() => 
    generateNextStep(leadData, leadScore, analytics, messages),
    [leadData, leadScore, analytics, messages]
  );

  const topics = useMemo(() => 
    extractTopics(analytics, messages), 
    [analytics, messages]
  );

// Generate sparkline data from real history
const sparklineData = useMemo(() => {
  if (scoreHistory.length >= 2) {
    return {
      motivation: scoreHistory.map(s => Number(s.motivation_score) || 50),
      hesitation: scoreHistory.map(s => Number(s.hesitation_score) || 50),
      responseTime: scoreHistory.map(s => Math.min(30, (Number(s.avg_reply_delay) || 0) / 60)), // Convert to minutes, cap at 30
      depth: scoreHistory.map(s => Number(s.conversation_depth) || 0)
    };
  }
  // Fallback: single point from current score
  const m = Number(leadScore?.motivation_score) || 50;
  const h = Number(leadScore?.hesitation_score) || 50;
  const r = Math.min(30, (Number(leadScore?.avg_reply_delay) || 0) / 60);
  const d = Number(leadScore?.conversation_depth) || 0;
  return {
    motivation: [m],
    hesitation: [h],
    responseTime: [r],
    depth: [d]
  };
}, [scoreHistory, leadScore]);

  // Action handlers
  const handleCall = () => {
    if (leadData?.phone) {
      setShowCallModal(true);
    }
  };

  const insertCallEvent = async (label) => {
    const now = new Date().toISOString();
    await supabase.from('messages').insert({
      direction: 'outbound',
      message_body: `Call logged — ${label}`,
      timestamp: now,
      phone: leadData.phone,
      tenant_id: leadData.tenant_id,
      lead_id: leadData.id,
      sender: 'Manual',
      channel: 'call',
      message_id: `call-log-${Date.now()}`,
      status: 'logged',
    });
    const { data: refreshed } = await supabase
      .from('messages')
      .select('*')
      .eq('lead_id', leadData.id)
      .order('timestamp', { ascending: true });
    if (refreshed) setMessages(refreshed);
  };

  const handleCallDisposition = async (outcome) => {
    if (outcome === 'won') {
      setShowCallModal(false);
      await insertCallEvent('Won');
      setShowWonModal(true);
      return;
    }
    if (outcome === 'lost') {
      setShowCallModal(false);
      setSavingAction(true);
      try {
        await supabase.from('leads').update({ status: 'Lost', lost_at: new Date().toISOString(), disposition_at: new Date().toISOString(), disposition_reason: 'explicit_decline' }).eq('id', leadData.id);
        await supabase.from('activity_feed').insert({
          tenant_id: leadData.tenant_id,
          event_type: 'lead_lost',
          title: 'Lead marked as Lost',
          lead_id: leadData.id,
          lead_name: leadData.name,
        });
        await insertCallEvent('Lost');
        setLeadData(prev => ({ ...prev, status: 'Lost' }));
      } catch (err) {
        console.error('Error marking lost:', err);
      } finally {
        setSavingAction(false);
      }
      return;
    }
    // connected / voicemail / no_answer / wrong_number — handleLogCall inserts the event
    await handleLogCall(outcome);
    setShowCallModal(false);
  };

  const handleSendSms = async () => {
    if (!smsMessage.trim() || !leadData) return;

    setSendingMessage(true);
    try {
      // Web-chat leads have no phone and no Twilio route — they carry a session_id, and the
      // widget renders any outbound row on that session via Supabase Realtime (the exact path
      // generate-web-chat-reply uses for AI replies; chat-widget.js onReply renders every
      // outbound row regardless of whether sender is 'AI' or 'Manual'). So a manual reply is
      // just an inserted row with channel='web' + session_id — no send-manual-sms call.
      const isWebLead = !!leadData.session_id;
      if (isWebLead) {
        const { error: webInsertError } = await supabase
          .from('messages')
          .insert({
            direction: 'outbound',
            message_body: smsMessage.trim(),
            timestamp: new Date().toISOString(),
            tenant_id: leadData.tenant_id,
            lead_id: leadData.id,
            sender: 'Manual',
            channel: 'web',
            session_id: leadData.session_id, // widget subscribes on this field
            message_id: `manual-web-${Date.now()}`,
            status: 'sent',
          });

        if (webInsertError) throw webInsertError;

        // A human is now in the thread — pause the AI so it doesn't reply over the manual
        // message. Stamp 'manual_off' (matching handleToggleAiConversation) so re-enabling
        // via the AI toggle cleanly clears any awaiting_human_reply the web silence gate set.
        if (leadData.ai_conversation_enabled) {
          await supabase
            .from('leads')
            .update({
              ai_conversation_enabled: false,
              disposition_reason: 'manual_off',
              disposition_at: new Date().toISOString(),
            })
            .eq('id', leadData.id);
          setLeadData(prev => ({ ...prev, ai_conversation_enabled: false }));
        }

        const { data: webMsgs } = await supabase
          .from('messages')
          .select('*')
          .eq('lead_id', leadId)
          .order('timestamp', { ascending: true });
        if (webMsgs) setMessages(webMsgs);
        setSmsMessage('');
        setShowSmsModal(false);
        return;
      }

      // Messenger leads (leads.channel='messenger') have no phone and no session_id — the
      // ONE Messenger send site is _shared/messengerSend.ts, reached here through the
      // send-manual-messenger edge function (the Messenger twin of send-manual-sms). It
      // applies the 24-hour window, the kill switch and the connected-Page check exactly as
      // an AI reply would, and it records the message row itself (there is nothing to
      // pre-insert or patch here, unlike the SMS branch below).
      const isMessengerLead = leadData.channel === 'messenger';
      if (isMessengerLead) {
        const { data: mgrResult, error: mgrError } = await supabase.functions.invoke('send-manual-messenger', {
          body: { lead_id: leadData.id, body: smsMessage.trim() },
        });
        if (mgrError || !mgrResult?.success) {
          // A non-2xx (e.g. 502 window closed) comes back as an error with data null; the
          // function's plain-English reason is in the response body.
          let reasonText = mgrResult?.error;
          if (!reasonText && mgrError?.context && typeof mgrError.context.json === 'function') {
            try { reasonText = (await mgrError.context.json())?.error; } catch (_) { /* body unreadable */ }
          }
          throw new Error(reasonText || mgrError?.message || 'Failed to send Messenger reply');
        }

        // A human is now in the thread — pause the AI, same as the web branch above.
        if (leadData.ai_conversation_enabled) {
          await supabase
            .from('leads')
            .update({
              ai_conversation_enabled: false,
              disposition_reason: 'manual_off',
              disposition_at: new Date().toISOString(),
            })
            .eq('id', leadData.id);
          setLeadData(prev => ({ ...prev, ai_conversation_enabled: false }));
        }

        const { data: mgrMsgs } = await supabase
          .from('messages')
          .select('*')
          .eq('lead_id', leadId)
          .order('timestamp', { ascending: true });
        if (mgrMsgs) setMessages(mgrMsgs);
        setSmsMessage('');
        setShowSmsModal(false);
        return;
      }

      const { data: campaignData, error: campaignError } = await supabase
        .from("campaigns")
        .select(`id, phone_number_id, phone_numbers (phone_number)`)
        .eq("id", leadData.campaign_id)
        .single();

      if (campaignError || !campaignData?.phone_numbers?.phone_number) {
        throw new Error('Campaign phone number not found');
      }

      const { data: insertedMessage, error: insertError } = await supabase
        .from('messages')
        .insert({
          direction: 'outbound',
          message_body: smsMessage.trim(),
          timestamp: new Date().toISOString(),
          phone: leadData.phone,
          tenant_id: leadData.tenant_id,
          lead_id: leadData.id,
          sender: 'Manual',
          channel: 'sms',
          message_id: `manual-${Date.now()}`,
          status: 'queued'
        })
        .select('id')
        .single();

      if (insertError) throw insertError;

      const { data: twilioResult, error: twilioError } = await supabase.functions.invoke('send-manual-sms', {
        body: {
          to: leadData.phone,
          from: campaignData.phone_numbers.phone_number,
          body: smsMessage.trim(),
          message_id: insertedMessage.id,
          tenant_id: leadData.tenant_id
        }
      });

      if (twilioError || !twilioResult?.success) {
        throw new Error(twilioResult?.error || 'Failed to send SMS');
      }

      await supabase
        .from('messages')
        .update({ message_id: twilioResult.twilio_sid, status: 'sent' })
        .eq('id', insertedMessage.id);

      if (leadData.ai_conversation_enabled) {
  await supabase
    .from('leads')
    .update({ ai_conversation_enabled: false })
    .eq('id', leadData.id);
  setLeadData(prev => ({ ...prev, ai_conversation_enabled: false }));
}

      const { data: msgs } = await supabase
        .from('messages')
        .select('*')
        .eq('lead_id', leadId)
        .order('timestamp', { ascending: true });
      
      if (msgs) setMessages(msgs);
      setSmsMessage('');
      setShowSmsModal(false);
    } catch (err) {
      console.error('Error sending SMS:', err);
      alert('Failed to send message: ' + err.message);
    } finally {
      setSendingMessage(false);
    }
  };

  const handleLogCall = async (outcome) => {
  setSavingAction(true);
  try {
    const now = new Date().toISOString();
    const isFirstCall = !leadData.first_call_at;
    
    await supabase
      .from('leads')
      .update({ 
        call_logged: true,
        call_outcome: outcome,
        last_call_at: now,
        first_call_at: isFirstCall ? now : leadData.first_call_at,
        total_call_attempts: (leadData.total_call_attempts || 0) + 1
      })
      .eq('id', leadData.id);

    await supabase.from('activity_feed').insert({
      tenant_id: leadData.tenant_id,
      event_type: 'call_logged',
      title: `Call logged: ${outcome.replace('_', ' ')}`,
      lead_id: leadData.id,
      lead_name: leadData.name
    });

    const outcomeLabels = {
      connected: 'Connected',
      voicemail: 'Left Voicemail',
      no_answer: 'No Answer',
      wrong_number: 'Wrong Number',
    };
    await supabase.from('messages').insert({
      direction: 'outbound',
      message_body: `Call logged — ${outcomeLabels[outcome] || outcome.replace('_', ' ')}`,
      timestamp: now,
      phone: leadData.phone,
      tenant_id: leadData.tenant_id,
      lead_id: leadData.id,
      sender: 'Manual',
      channel: 'call',
      message_id: `call-log-${Date.now()}`,
      status: 'logged',
    });

    // Refresh messages so the call event appears in the conversation
    const { data: refreshed } = await supabase
      .from('messages')
      .select('*')
      .eq('lead_id', leadData.id)
      .order('timestamp', { ascending: true });
    if (refreshed) setMessages(refreshed);

    setLeadData(prev => ({
      ...prev,
      call_logged: true,
      call_outcome: outcome,
      total_call_attempts: (prev.total_call_attempts || 0) + 1
    }));
    setShowLogCallModal(false);
  } catch (err) {
    console.error('Error logging call:', err);
    alert('Failed to log call');
  } finally {
    setSavingAction(false);
  }
};

  // A snooze does not expire. leads.snoozed_until stays the wire format — every
  // suppression gate (process-message-queue, ai-initial-outreach, ai-followup-outreach)
  // already reads "a future timestamp means stay quiet" — so indefinite is expressed
  // as a date that never arrives rather than a new column, which keeps those five
  // deployed edge functions untouched. Anything past SNOOZE_INDEFINITE_AFTER_MS is
  // rendered as "until you wake them", never as a countdown.
  const handleSnooze = async () => {
    setSavingAction(true);
    try {
      const { error: upErr } = await supabase
        .from('leads')
        .update({ snoozed_until: SNOOZE_FOREVER })
        .eq('id', leadData.id);
      if (upErr) throw upErr;

      const { error: logErr } = await supabase.from('activity_feed').insert({
        tenant_id: leadData.tenant_id,
        event_type: 'lead_snoozed',
        title: 'Lead snoozed — SurFox paused until woken',
        lead_id: leadData.id,
        lead_name: leadData.name
      });
      if (logErr) console.error('Snooze log failed (non-critical):', logErr.message);

      setLeadData(prev => ({ ...prev, snoozed_until: SNOOZE_FOREVER }));
      setShowSnoozeModal(false);
    } catch (err) {
      console.error('Error snoozing lead:', err);
      alert('Failed to snooze lead');
    } finally {
      setSavingAction(false);
    }
  };

  // No manual wake handler yet, by design. The snooze releases only when the lead
  // texts back — handleincomingmessage and handle-web-chat-message clear
  // snoozed_until on any inbound. Restore a wake action here when that control is built.

  const handleWon = async (dealValue) => {
  setSavingAction(true);
  try {
    await supabase
      .from('leads')
      .update({ 
        status: 'Won', 
        converted_at: new Date().toISOString(),
        estimated_pipeline_value: dealValue,
        ai_conversation_enabled: false
      })
      .eq('id', leadData.id);

    await supabase.from('activity_feed').insert({
      tenant_id: leadData.tenant_id,
      event_type: 'lead_won',
      title: `Lead marked as Won - $${dealValue.toLocaleString()}`,
      lead_id: leadData.id,
      lead_name: leadData.name
    });

    setLeadData(prev => ({ ...prev, status: 'Won', estimated_pipeline_value: dealValue, ai_conversation_enabled: false }));
    setShowWonModal(false);
  } catch (err) {
    console.error('Error marking won:', err);
    alert('Failed to update lead');
  } finally {
    setSavingAction(false);
  }
};

  const handleTogglePin = async () => {
    const newValue = !leadData?.pinned_to_dashboard;
    try {
      const { error } = await supabase
        .from('leads')
        .update({ pinned_to_dashboard: newValue })
        .eq('id', leadData.id);
      if (error) throw error;
      setLeadData(prev => ({ ...prev, pinned_to_dashboard: newValue }));
    } catch (err) {
      console.error('Error toggling pin:', err);
      alert('Failed to update pin status');
    }
  };

  const handleLost = async () => {
    if (!window.confirm('Mark this lead as Lost?')) return;
    setSavingAction(true);
    try {
      await supabase
        .from('leads')
        .update({ status: 'Lost', lost_at: new Date().toISOString(), disposition_at: new Date().toISOString(), disposition_reason: 'explicit_decline' })
        .eq('id', leadData.id);

      await supabase.from('activity_feed').insert({
        tenant_id: leadData.tenant_id,
        event_type: 'lead_lost',
        title: `Lead marked as Lost`,
        lead_id: leadData.id,
        lead_name: leadData.name
      });

      setLeadData(prev => ({ ...prev, status: 'Lost' }));
    } catch (err) {
      console.error('Error marking lost:', err);
      alert('Failed to update lead');
    } finally {
      setSavingAction(false);
    }
  };

  const handleToggleAiConversation = async () => {
    const newValue = !(leadData.ai_conversation_enabled !== false);

    // What "AI off" MEANS is channel-specific, so the toggle has to know which channel it
    // is looking at. Web leads carry session_id, SMS leads carry phone — the same
    // discriminator usePriorityQueue uses.
    //
    //   WEB — the visitor is sitting in a live widget, and handle-web-chat-message answers
    //         their next message with "someone will follow up with you shortly". That is a
    //         PROMISE, so the lead has to reach a human. handoff_triggered is what the
    //         priority queue filters on and marked_hot_at is its sort/date key (a
    //         "surfaced-to-human at" timestamp, NOT a hotness signal — status is left alone
    //         so the hot pipeline is not inflated; see reference_warm_handoff_vs_hot_lead).
    //         Without this the AI goes quiet, the visitor is told help is coming, and
    //         nobody is ever told.
    //   SMS — nobody is waiting in real time and handleincomingmessage stays SILENT rather
    //         than promising anything, so "AI off" here can equally mean "stop texting
    //         them". Left as a plain suppression: forcing every SMS shutoff into the queue
    //         would fill needs-you-now with leads nobody is waiting on.
    // Messenger is the same promise-bound shape as web (a person on a live-ish chat, no
    // silent "stop texting them" reading), so it shares web's branch below rather than
    // SMS's plain suppression.
    const isWebLead = !!leadData.session_id;
    const isMessengerLead = leadData.channel === 'messenger';
    const isPromiseBound = isWebLead || isMessengerLead;

    // Optimistic update — mirror the handoff fields too, so the UI does not keep showing
    // a web/Messenger lead as live/un-handed-off until the next refetch.
    setLeadData(prev => ({
      ...prev,
      ai_conversation_enabled: newValue,
      ...(isPromiseBound
        ? (newValue
            ? (prev.disposition_reason === 'manual_off'
                ? { handoff_triggered: false, marked_hot_at: null, awaiting_human_reply: false }
                : {})
            : { handoff_triggered: true })
        : {}),
    }));

    try {
      // Record WHY the AI went off. Without this a manual shutoff is indistinguishable
      // from a wrong-number kill, a quality-monitor kill, or an accident — the lead just
      // goes silent with no reason on the row. Cleared on re-enable so a stale reason
      // can't mislabel a lead that's live again.
      // NOTE: 'manual_off' must stay in the leads_disposition_reason_check CHECK
      // constraint. It was missing 2026-07-17..20 and every disable failed with 23514.
      //
      // Re-enable must UNDO a handoff THIS toggle created, or the conversation stays dead:
      // the web silence gate keys off handoff_triggered, so a lead left flagged keeps
      // getting the follow-up ack no matter how many times AI is switched back on. That is
      // exactly the trap that bricked lead f64aea7e. Guarded on disposition_reason ===
      // 'manual_off' so re-enabling never clears a REAL handoff (booking, info request).
      const undoManualHandoff = isPromiseBound && leadData.disposition_reason === 'manual_off';

      const update = newValue
        ? {
            ai_conversation_enabled: true,
            disposition_reason: null,
            disposition_at: null,
            ...(undoManualHandoff
              ? { handoff_triggered: false, marked_hot_at: null, awaiting_human_reply: false }
              : {}),
          }
        : {
            ai_conversation_enabled: false,
            disposition_reason: 'manual_off',
            disposition_at: new Date().toISOString(),
            ...(isPromiseBound
              ? { handoff_triggered: true, marked_hot_at: new Date().toISOString() }
              : {}),
          };

      const { error } = await supabase
        .from('leads')
        .update(update)
        .eq('id', leadData.id);

      if (error) throw error;

      // Log activity — metadata carries WHO flipped it, since leads has no disposition_by.
      // Deliberately outside the throw path: the toggle above has already committed, so a
      // failed audit write must never revert the UI or tell the user the shutoff failed.
      try {
        const { error: feedError } = await supabase.from('activity_feed').insert({
          tenant_id: leadData.tenant_id,
          event_type: 'lead_updated',
          title: `AI conversation ${newValue ? 'enabled' : 'disabled'} for ${leadData.name}`,
          lead_id: leadData.id,
          lead_name: leadData.name,
          metadata: {
            source: 'lead_detail_toggle',
            ai_conversation_enabled: newValue,
            disposition_reason: newValue ? null : 'manual_off',
            changed_by_user_id: session?.user?.id || null,
            changed_by_email: session?.user?.email || null,
          }
        });
        if (feedError) console.error('Activity feed log failed (toggle still saved):', feedError);
      } catch (feedErr) {
        console.error('Activity feed log failed (toggle still saved):', feedErr);
      }

    } catch (err) {
      console.error('Error toggling AI conversation:', err);
      alert('Failed to update AI conversation setting');
      // Revert on error
      setLeadData(prev => ({ ...prev, ai_conversation_enabled: !newValue }));
    }
  };

  const handleCreatePdf = async () => {
    // Load logo as data URL
    let logoDataUrl = null;
    try {
      const resp = await fetch('/logo.png');
      const blob = await resp.blob();
      logoDataUrl = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });
    } catch (e) { /* logo is optional */ }

    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 18;
    const contentW = pageW - margin * 2;
    let y = margin;

    const addPage = () => { doc.addPage(); y = margin; };
    const checkPage = (needed = 20) => { if (y + needed > pageH - margin) addPage(); };

    // ── Header bar with branding ──
    doc.setFillColor(249, 115, 22); // orange-500
    doc.rect(0, 0, pageW, 14, 'F');

    // Logo in header
    const logoSize = 10;
    if (logoDataUrl) {
      doc.addImage(logoDataUrl, 'PNG', margin, 2, logoSize, logoSize);
    }
    const textStart = logoDataUrl ? margin + logoSize + 3 : margin;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(255, 255, 255);
    doc.text('SurFox AI', textStart, 9);
    const brandW = doc.getTextWidth('SurFox AI');
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text('|  Conversation Report', textStart + brandW + 4, 9);
    // Date on right
    doc.setFontSize(8);
    const dateStr = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    doc.text(dateStr, pageW - margin - doc.getTextWidth(dateStr), 9);

    // ── Lead info row ──
    y = 24;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.setTextColor(17, 24, 39);
    doc.text(lead.name, margin, y);

    // Status badge inline
    const statusText = lead.state || 'Unknown';
    doc.setFontSize(8);
    const badgeW = doc.getTextWidth(statusText) + 6;
    const nameW = doc.getTextWidth(lead.name);
    doc.setFontSize(16); // reset to measure name width properly
    const actualNameW = doc.getTextWidth(lead.name);
    const badgeX = margin + actualNameW + 5;
    doc.setFillColor(249, 115, 22);
    doc.roundedRect(badgeX, y - 4.5, badgeW + 2, 6.5, 1.5, 1.5, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(255, 255, 255);
    doc.text(statusText, badgeX + 4, y - 0.5);

    // Phone on same line, right-aligned
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(107, 114, 128);
    const phoneStr = lead.phone || 'No phone';
    doc.text(phoneStr, pageW - margin - doc.getTextWidth(phoneStr), y);

    // ── Summary sentence (compact inline) ──
    if (insights.length > 0 && insights[0]?.text) {
      y += 8;
      const summaryText = insights[0].text;
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(8.5);
      doc.setTextColor(120, 113, 108);
      const summaryLines = doc.splitTextToSize(summaryText, contentW);
      doc.text(summaryLines.slice(0, 2), margin, y);
      y += summaryLines.slice(0, 2).length * 3.5 + 2;
    }

    // ── Thin divider ──
    y += 4;
    doc.setDrawColor(229, 231, 235);
    doc.setLineWidth(0.2);
    doc.line(margin, y, pageW - margin, y);
    y += 6;

    // ── Conversation heading + count inline ──
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(17, 24, 39);
    doc.text('Conversation', margin, y);
    const convLabelW = doc.getTextWidth('Conversation');
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(156, 163, 175);
    doc.text(`${messages.length} messages`, margin + convLabelW + 4, y);
    y += 7;

    // ── Messages (use messageGroups which has isFollowUp properly computed) ──
    messageGroups.forEach((group) => {
      group.messages.forEach((msg) => {
        const isInbound = msg.direction === 'inbound';
        const isFollowUp = msg.isFollowUp;

        const senderLabel = isInbound ? (lead.name || 'Lead') : (msg.isAI ? 'SurFox AI' : 'You');
        const body = msg.text || '';
        const time = msg.time || '';

        const wrappedLines = doc.splitTextToSize(body, contentW - 16);
        const followUpBannerH = isFollowUp ? 4.5 : 0;
        const senderRowH = 3;
        const gapH = 2.5;
        const textBlockH = wrappedLines.length * 3.5;
        const innerContentH = senderRowH + gapH + textBlockH;
        const padV = Math.max(3, (innerContentH + 6 - innerContentH) / 2); // min 3mm top/bottom
        const blockH = innerContentH + padV * 2 + followUpBannerH;
        checkPage(blockH + 2.5);

        // Bubble background
        const bubbleColor = isFollowUp ? [255, 247, 237] : isInbound ? [239, 246, 255] : [243, 244, 246];
        doc.setFillColor(...bubbleColor);
        doc.roundedRect(margin + 2, y, contentW - 4, blockH, 1.5, 1.5, 'F');

        // Direction indicator bar
        const barColor = isFollowUp ? [234, 88, 12] : isInbound ? [59, 130, 246] : [249, 115, 22];
        doc.setFillColor(...barColor);
        doc.rect(margin + 2, y, 1.2, blockH, 'F');

        const bubbleTop = y;

        // Follow-up banner
        if (isFollowUp) {
          doc.setFillColor(255, 237, 213);
          doc.rect(margin + 3.2, y, contentW - 5.2, followUpBannerH, 'F');
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(6);
          doc.setTextColor(234, 88, 12);
          doc.text('Automated Follow-up', margin + 6, y + 3.2);
          y += followUpBannerH;
        }

        // Vertically center content within remaining bubble space
        y += padV + senderRowH;

        // Sender name + time
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7.5);
        doc.setTextColor(isInbound ? 29 : 107, isInbound ? 78 : 114, isInbound ? 216 : 128);
        doc.text(senderLabel, margin + 6, y);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(6.5);
        doc.setTextColor(156, 163, 175);
        doc.text(time, pageW - margin - doc.getTextWidth(time) - 4, y);

        // Message body
        y += gapH;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(55, 65, 81);
        wrappedLines.forEach(line => {
          y += 3.5;
          doc.text(line, margin + 6, y);
        });

        // Jump to end of bubble
        y = bubbleTop + blockH + 2.5;
      });
    });

    // ── Footer on every page ──
    const totalPages = doc.getNumberOfPages();
    for (let p = 1; p <= totalPages; p++) {
      doc.setPage(p);
      // Footer line
      doc.setDrawColor(249, 115, 22);
      doc.setLineWidth(0.4);
      doc.line(margin, pageH - 12, pageW - margin, pageH - 12);
      // Footer text
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);
      doc.setTextColor(249, 115, 22);
      doc.text('SurFox', margin, pageH - 8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(156, 163, 175);
      doc.text('AI | Conversation Report', margin + doc.getTextWidth('SurFox '), pageH - 8);
      doc.text(`Page ${p} of ${totalPages}`, pageW - margin - doc.getTextWidth(`Page ${p} of ${totalPages}`), pageH - 8);
    }

    doc.save(`${(lead.name || 'lead').replace(/\s+/g, '_')}_conversation.pdf`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 rounded-full border-2 border-orange-500 border-t-transparent animate-spin" />
          <span className="text-sm text-gray-500">Loading intelligence...</span>
        </div>
      </div>
    );
  }

  if (error || !lead) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-center">
          <AlertTriangle className="w-10 h-10 text-amber-500" />
          <span className="text-sm text-gray-700 font-medium">Failed to load lead</span>
          <span className="text-xs text-gray-500">{error || 'Lead not found'}</span>
        </div>
      </div>
    );
  }

  return (
  <div className="tour-lead-detail min-h-screen bg-gray-50 pb-20 lg:pb-24">
    <StateBar lead={lead} onToggleAi={handleToggleAiConversation} />

      <div className="max-w-7xl mx-auto px-4 lg:px-6 py-4 lg:py-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7 space-y-6">
            <NarrativeSummary insights={insights} />
            <MessageStream messages={messageGroups} timezone={timezone} />
          </div>

          <div className="lg:col-span-5 space-y-6">
            <NextStepModule recommendation={nextStep} />
            
            <div className="grid grid-cols-2 gap-3 lg:gap-4">
              <InsightPanel 
                title="Motivation"
                value={`${lead.motivationScore}%`}
                trend={analytics?.engagement_timeline?.motivation_changes > 0 ? 'up' : 'down'}
                trendValue={analytics?.engagement_timeline?.motivation_changes 
                  ? `${analytics.engagement_timeline.motivation_changes > 0 ? '+' : ''}${Math.round(analytics.engagement_timeline.motivation_changes)}%`
                  : '—'}
                sparkData={sparklineData.motivation}
                color="#EF4444"
                icon={Flame}
              />
              <InsightPanel 
                title="Hesitation"
                value={`${lead.hesitationScore}%`}
                trend={analytics?.engagement_timeline?.hesitation_changes < 0 ? 'down' : 'up'}
                trendValue={analytics?.engagement_timeline?.hesitation_changes
                  ? `${Math.round(analytics.engagement_timeline.hesitation_changes)}%`
                  : '—'}
                sparkData={sparklineData.hesitation}
                color="#F59E0B"
                icon={AlertTriangle}
              />
              <InsightPanel 
                title="Avg Response"
                value={lead.responseTime > 0 ? `${lead.responseTime}m` : '—'}
                trend={lead.responseTime < 10 ? 'up' : 'down'}
                trendValue={lead.responseTime < 5 ? 'Fast' : lead.responseTime < 15 ? 'Normal' : 'Slow'}
                sparkData={sparklineData.responseTime}
                color="#3B82F6"
                icon={Clock}
              />
              <InsightPanel 
                title="Depth"
                value={`${lead.conversationDepth}`}
                trend="up"
                trendValue={`+${Math.max(1, Math.floor(lead.conversationDepth / 3))}`}
                sparkData={sparklineData.depth}
                color="#8B5CF6"
                icon={BarChart3}
              />
            </div>

            <TopicMap topics={topics} />

            <LeadInfoCard lead={leadData} timezone={timezone} />

            <UnansweredQuestionsCard
              questions={unansweredQuestions}
              appointment={scheduledAppointment}
              timezone={timezone}
            />
          </div>
        </div>
      </div>

      <ActionsBar
        lead={lead}
        onCall={handleCall}
        onSms={() => setShowSmsModal(true)}
        onLogCall={() => setShowLogCallModal(true)}
        onSnooze={() => setShowSnoozeModal(true)}
        onWon={() => setShowWonModal(true)}
        onLost={handleLost}
        isPinned={!!leadData?.pinned_to_dashboard}
        onTogglePin={handleTogglePin}
        onCreatePdf={handleCreatePdf}
        smsOptedOut={!!leadData?.sms_opted_out}
        isWebLead={!!leadData?.session_id}
        isMessengerLead={leadData?.channel === 'messenger'}
        messengerOptedOut={!!leadData?.opted_out}
        onEscalate={() => setShowEscalationModal(true)}
        onPush={() => setShowPushModal(true)}
        showPush={hasIntegrations}
      />

      <SmsModal 
        isOpen={showSmsModal}
        onClose={() => setShowSmsModal(false)}
        onSend={handleSendSms}
        message={smsMessage}
        setMessage={setSmsMessage}
        sending={sendingMessage}
        leadName={lead.name}
        aiEnabled={lead.ai_conversation_enabled !== false}
        isWebLead={!!leadData?.session_id}
        isMessengerLead={leadData?.channel === 'messenger'}
      />

      <LogCallModal 
        isOpen={showLogCallModal}
        onClose={() => setShowLogCallModal(false)}
        onLog={handleLogCall}
        saving={savingAction}
      />

      <SnoozeModal 
        isOpen={showSnoozeModal}
        onClose={() => setShowSnoozeModal(false)}
        onSnooze={handleSnooze}
        saving={savingAction}
      />

      <WonModal
        isOpen={showWonModal}
        onClose={() => setShowWonModal(false)}
        onSubmit={handleWon}
        saving={savingAction}
        existingValue={leadData?.estimated_pipeline_value}
      />

      <EscalationModal
        isOpen={showEscalationModal}
        onClose={() => setShowEscalationModal(false)}
        leadId={leadId}
        leadName={lead?.name}
      />

      <PushToCrmModal
        isOpen={showPushModal}
        onClose={() => setShowPushModal(false)}
        leadId={leadId}
        leadName={lead?.name}
      />

      {showCallModal && (
        <CallModal
          isOpen={showCallModal}
          lead={lead}
          isReady={twilioCall.isReady}
          twilioError={twilioCall.error}
          callStatus={twilioCall.callStatus}
          callDuration={twilioCall.callDuration}
          isMuted={twilioCall.isMuted}
          makeCall={twilioCall.makeCall}
          hangup={twilioCall.hangup}
          toggleMute={twilioCall.toggleMute}
          formatDuration={twilioCall.formatDuration}
          onDisposition={handleCallDisposition}
          onSkip={() => setShowCallModal(false)}
        />
      )}
    </div>
  );
};

export default LeadDetail;